import { Link, useNavigate, useLocation } from "react-router-dom";
import api, { mediaUrl, isPlatformHostname, extractErr } from "@/lib/api";
import { useCart } from "@/lib/cart";
import { useCartUI } from "@/lib/cartui";
import { useWishlist } from "@/lib/wishlist";
import { useCustomerAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { ShoppingCart, User, LogOut, Phone, Menu, Instagram, Facebook, Youtube, Twitter, MessageSquareText, Heart, Wallet, Search, ChevronUp, ChevronDown, ChevronLeft, ChevronRight, Mail, Home, ArrowRight, Flame, ShoppingBag, Mic, Send, MapPin, Percent, LayoutGrid, Zap } from "lucide-react";
import { useState, useEffect, useLayoutEffect, useRef } from "react";
import { toast } from "sonner";
import StorefrontCartDrawer from "./StorefrontCartDrawer";
import { StorefrontSearchPopup, StorefrontSearchTrigger } from "./StorefrontSearchPopup";
import StoreHead from "@/components/StoreHead";
import StoreAds from "@/components/StoreAds";
import StorefrontFingerprint from "@/components/StorefrontFingerprint";
import StoreWhatsAppButton from "@/components/StoreWhatsAppButton";
import { fontStack, googleFontHref } from "@/lib/googleFonts";
import { useStoreBase } from "@/lib/store";
import { useTranslation } from "react-i18next";
import GoogleStorefrontTranslate from "@/components/GoogleStorefrontTranslate";
import { makeHref, SmartLink, menuChildren } from "./StorefrontNav";
import StorefrontBlogSection from "./StorefrontBlogSection";
import AnnouncementBar from "./AnnouncementBar";
import { CosmosNewsletter } from "./StorefrontHomeCosmos";
import { isModernStorefront, isTrendyStorefront, isPerfectoStorefront, isBoxyStorefront, isMedicoStorefront, isMixyStorefront, isVariousStorefront, isBeastStorefront, isLocallyStorefront, isQuickstoStorefront, isBigshowStorefront, isFurnisStorefront, isCarPartsStorefront, isCosmosStorefront, isOndemandStorefront, zeploFeaturesOn, resolveStoreColors, storefrontThemeId } from "@/lib/storefrontTheme";
import { getOndemandPin, getOndemandPinLabel } from "@/lib/ondemandPin";
import "./storefrontBeast.css";
import "./storefrontBigShow.css";
import "./storefrontFurnis.css";
import "./storefrontCarParts.css";
import "./storefrontCosmos.css";
import "./storefrontOndemand.css";

function NavItem({ base, item, testid, onClick, className, desktop, dropdownClassName, dropdownLinkClassName, wrapClassName }) {
  const kids = menuChildren(item);
  if (!kids.length) {
    return <SmartLink base={base} item={item} testid={testid} onClick={onClick} className={className} />;
  }
  if (!desktop) {
    return (
      <div data-testid={`${testid}-group`}>
        <SmartLink base={base} item={item} testid={testid} onClick={onClick} className={className} />
        <div className="pl-4 mt-2 flex flex-col gap-2">
          {kids.map((c, j) => (
            <SmartLink
              key={j}
              base={base}
              item={c}
              onClick={onClick}
              className="text-base font-medium text-stone-600"
              testid={`${testid}-sub-${j}`}
            />
          ))}
        </div>
      </div>
    );
  }
  return (
    <div className={`relative group/nav ${wrapClassName || ""}`} data-testid={`${testid}-wrap`}>
      <SmartLink
        base={base}
        item={item}
        testid={testid}
        onClick={onClick}
        className={`${className || ""} inline-flex items-center gap-1`}
      >
        {item.label} <ChevronDown className="w-3.5 h-3.5 opacity-70" />
      </SmartLink>
      <div className="absolute left-0 top-full pt-2 hidden group-hover/nav:block group-focus-within/nav:block z-[120] min-w-[13.5rem]">
        <div className={dropdownClassName || "bg-white border border-stone-200 rounded-xl shadow-lg py-2"} data-testid={`${testid}-submenu`}>
          {kids.map((c, j) => (
            <SmartLink
              key={j}
              base={base}
              item={c}
              className={dropdownLinkClassName || "block px-3 py-1.5 text-sm text-stone-700 hover:bg-stone-50"}
              testid={`${testid}-sub-${j}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function CosmosMenuRow({ menu, base, onOpenMenu }) {
  const slotRef = useRef(null);
  const measureRef = useRef(null);
  const [compact, setCompact] = useState(false);
  useLayoutEffect(() => {
    const slot = slotRef.current;
    const measure = measureRef.current;
    if (!slot || !measure) return undefined;
    const check = () => {
      const parents = (menu || []).filter((m) => menuChildren(m).length).length;
      setCompact(measure.scrollWidth + parents * 18 > slot.clientWidth + 1);
    };
    check();
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(check) : null;
    if (ro) ro.observe(slot);
    window.addEventListener("resize", check);
    return () => {
      window.removeEventListener("resize", check);
      if (ro) ro.disconnect();
    };
  }, [menu]);
  const linkClass = "cosmos-nav-link whitespace-nowrap";
  return (
    <div ref={slotRef} className="relative min-w-0 h-10 flex items-center" data-testid="cosmos-menu-slot" data-cosmos-compact={compact ? "true" : "false"}>
      <div ref={measureRef} aria-hidden="true" className="absolute left-0 top-0 invisible pointer-events-none flex items-center gap-1 whitespace-nowrap w-max">
        {(menu || []).map((m, i) => (
          <span key={i} className={linkClass}>{m.label}</span>
        ))}
      </div>
      {compact ? (
        <button type="button" data-testid="cosmos-menu-btn-desktop" onClick={onOpenMenu} className="w-10 h-10 inline-flex items-center justify-center text-stone-900 rounded-full hover:bg-stone-100" aria-label="Menu">
          <Menu className="w-5 h-5" />
        </button>
      ) : (
        <nav className="flex items-center justify-start gap-1 text-sm font-medium text-stone-900 min-w-0" data-testid="cosmos-menu-row">
          {(menu || []).map((m, i) => (
            <NavItem
              key={i}
              base={base}
              item={m}
              className={linkClass}
              wrapClassName="cosmos-nav-item"
              dropdownClassName="cosmos-nav-dropdown"
              dropdownLinkClassName="cosmos-nav-drop-link"
              testid={`nav-${i}`}
              desktop
            />
          ))}
        </nav>
      )}
    </div>
  );
}

function OndemandPinChip({ slug, onChange }) {
  const [pin, setPin] = useState(() => getOndemandPin(slug));
  const [label, setLabel] = useState(() => getOndemandPinLabel(slug));
  useEffect(() => {
    const on = (e) => {
      const d = e.detail;
      if (d && typeof d === "object") {
        setPin(d.pincode || getOndemandPin(slug));
        setLabel(d.label || getOndemandPinLabel(slug));
      } else {
        setPin(typeof d === "string" ? d : getOndemandPin(slug));
        setLabel(getOndemandPinLabel(slug));
      }
    };
    window.addEventListener("ondemand-pin", on);
    return () => window.removeEventListener("ondemand-pin", on);
  }, [slug]);
  return (
    <button
      type="button"
      onClick={onChange}
      className="inline-flex items-center gap-1.5 min-w-0 max-w-[11rem] sm:max-w-[16rem] text-left"
      data-testid="ondemand-header-pin"
    >
      <Zap className="w-4 h-4 shrink-0" style={{ color: "var(--store-primary, #7C3AED)" }} />
      <span className="min-w-0">
        <span className="block text-[9px] uppercase tracking-wide text-stone-400 leading-none">Delivery</span>
        <span className="block text-sm sm:text-[15px] font-extrabold text-stone-900 truncate leading-tight" data-testid="ondemand-header-label">{label || "Select Location"}</span>
        {pin ? <span className="block text-[11px] font-medium text-stone-500 leading-none mt-0.5 font-mono-nums" data-testid="ondemand-header-pincode">{pin}</span> : null}
      </span>
    </button>
  );
}

function OndemandCatMenu({ items, base, menu }) {
  const scroller = useRef(null);
  const [edges, setEdges] = useState({ left: false, right: false });
  const cats = (items || []).filter((c) => !c.parent_id).slice(0, 12);
  const list = cats.length ? cats.map((c) => ({ key: c.id, label: c.name, to: `${base}/category/${c.slug}` }))
    : (menu || []).map((m, i) => ({ key: i, label: m.label, to: m.path, item: m }));
  useEffect(() => {
    const el = scroller.current;
    if (!el) return undefined;
    const measure = () => {
      const max = el.scrollWidth - el.clientWidth;
      setEdges({
        left: el.scrollLeft > 4,
        right: max > 4 && el.scrollLeft < max - 4,
      });
    };
    const onWheel = (e) => {
      if (el.scrollWidth <= el.clientWidth + 1) return;
      if (e.deltaX !== 0 && Math.abs(e.deltaX) >= Math.abs(e.deltaY)) return;
      el.scrollLeft += e.deltaY;
      e.preventDefault();
    };
    measure();
    el.addEventListener("wheel", onWheel, { passive: false });
    el.addEventListener("scroll", measure, { passive: true });
    window.addEventListener("resize", measure);
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    if (ro) ro.observe(el);
    return () => {
      el.removeEventListener("wheel", onWheel);
      el.removeEventListener("scroll", measure);
      window.removeEventListener("resize", measure);
      if (ro) ro.disconnect();
    };
  }, [list.length]);
  if (!list.length) return null;
  const nudge = (dir) => {
    const el = scroller.current;
    if (!el) return;
    el.scrollBy({ left: dir * Math.max(180, Math.round(el.clientWidth * 0.55)), behavior: "smooth" });
  };
  const arrowCls = "hidden md:inline-flex absolute top-1/2 -translate-y-1/2 z-10 w-7 h-7 rounded-full bg-stone-100 text-stone-500 items-center justify-center shadow-sm hover:bg-stone-200";
  return (
    <div className="relative w-full border-t border-stone-100 bg-white" data-testid="ondemand-menu-row">
      {edges.left ? (
        <button type="button" className={`${arrowCls} left-1 sm:left-2`} onClick={() => nudge(-1)} aria-label="Scroll categories left" data-testid="ondemand-menu-scroll-left">
          <ChevronLeft className="w-4 h-4" />
        </button>
      ) : null}
      <div ref={scroller} className={`w-full min-w-0 max-w-[90rem] mx-auto px-3 sm:px-6 lg:px-8 flex items-center gap-1 overflow-x-auto overscroll-x-contain no-scrollbar py-1.5 ${(edges.left || edges.right) ? "md:px-10" : ""}`} data-testid="ondemand-menu-scroller">
        {list.map((it, i) => {
          const cls = "shrink-0 inline-flex items-center px-3 py-2 text-sm font-semibold text-stone-700 hover:text-stone-950 whitespace-nowrap";
          if (it.item) {
            return <NavItem key={it.key} base={base} item={it.item} className={cls} testid={`ondemand-menu-${i}`} desktop />;
          }
          return (
            <Link key={it.key} to={it.to} className={cls} data-testid={`ondemand-menu-${i}`}>
              {it.label}
            </Link>
          );
        })}
      </div>
      {edges.right ? (
        <button type="button" className={`${arrowCls} right-1 sm:right-2`} onClick={() => nudge(1)} aria-label="Scroll categories right" data-testid="ondemand-menu-scroll-right">
          <ChevronRight className="w-4 h-4" />
        </button>
      ) : null}
    </div>
  );
}

function CarPartsShopByCats({ categories, base }) {
  const [open, setOpen] = useState(false);
  const cats = (categories || []).filter((c) => c && c.slug);
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (!e.target.closest?.("[data-testid='carparts-shop-by-cats']")) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);
  return (
    <div className="relative shrink-0" data-testid="carparts-shop-by-cats">
      <button type="button" onClick={() => setOpen((v) => !v)} className="inline-flex items-center gap-2 h-11 px-3 sm:px-4 rounded-lg bg-stone-100 text-[11px] font-extrabold tracking-[0.08em] uppercase text-stone-800" data-testid="carparts-shop-by-cats-btn">
        <Menu className="w-4 h-4" /> Shop by categories <ChevronDown className="w-3.5 h-3.5 opacity-60" />
      </button>
      {open ? (
        <div className="absolute left-0 top-full z-[80] mt-1 w-64 max-h-80 overflow-y-auto bg-white border border-stone-200 rounded-xl shadow-lg py-2" data-testid="carparts-shop-by-cats-menu">
          {cats.map((c) => (
            <Link key={c.id} to={`${base}/category/${c.slug}`} onClick={() => setOpen(false)} className={`block px-3 py-2 text-sm text-stone-700 hover:bg-stone-50 ${c.parent_id ? "pl-6 text-stone-500" : "font-semibold"}`} data-testid={`carparts-cat-nav-${c.slug}`}>
              {c.name}
            </Link>
          ))}
          {!cats.length ? <div className="px-3 py-2 text-sm text-stone-400">No categories yet</div> : null}
        </div>
      ) : null}
    </div>
  );
}

function FooterBrand({ info, primary, dark }) {
  const src = info.footer_logo_url || info.logo_url;
  return (
    <Link to={`${info.base}/`} className="flex items-center gap-3" data-testid="footer-brand">
      {src ? (
        <img src={mediaUrl(src)} alt={info.name} className="h-11 w-auto max-w-[200px] object-contain" data-testid="store-footer-logo-img" />
      ) : (
        <>
          <div className="w-10 h-10 rounded-lg text-white flex items-center justify-center font-display font-bold" style={{ background: primary }}>
            {(info.name || "S").slice(0, 1).toUpperCase()}
          </div>
          <div className={`font-display font-bold text-lg ${dark ? "text-white" : "text-stone-900"}`}>{info.name}</div>
        </>
      )}
    </Link>
  );
}

function BrandMark({ info, primary, imgClass, wrapClass, testid, nameClass, logoUrl }) {
  const src = logoUrl || info.logo_url;
  return (
    <Link to={`${info.base}/`} className={wrapClass} data-testid={testid || "store-brand"}>
      {src ? (
        <img src={mediaUrl(src)} alt={info.name} className={imgClass} data-testid="store-logo-img" />
      ) : (
        <>
          <div className="w-9 h-9 rounded-lg text-white flex items-center justify-center font-display font-bold shrink-0" style={{ background: primary }}>
            {(info.name || "S").slice(0, 1).toUpperCase()}
          </div>
          <div className={nameClass || "font-display font-bold text-stone-900 truncate max-w-[140px] sm:max-w-none"}>{info.name}</div>
        </>
      )}
    </Link>
  );
}

function SearchToggle({ shopHref, navigate, testId = "store-search-toggle", look, className }) {
  return <StorefrontSearchTrigger shopHref={shopHref} navigate={navigate} testId={testId} look={look || "classic"} className={className} />;
}

function MixyCategorySearch({ shopHref, navigate, categories, testId = "mixy-search-bar", placeholder = "Search for anything...", allLabel = "All products", showMic = false, look = "mixy", variant = "field" }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      {variant === "icon" ? (
        <StorefrontSearchTrigger
          shopHref={shopHref}
          navigate={navigate}
          look={look}
          testId={testId}
          placeholder={placeholder}
          categories={categories}
          showCategory
          allLabel={allLabel}
          showMic={showMic}
        />
      ) : (
        <>
          <button
            type="button"
            data-testid={testId}
            onClick={() => setOpen(true)}
            className={`flex-1 min-w-0 w-full flex items-stretch h-11 overflow-hidden text-left ${look === "quicksto" ? "rounded-full border-0 bg-white" : look === "locally" ? "rounded-full border-0 bg-white" : look === "various" ? "rounded-xl border border-stone-200 bg-[#F1F2E8]" : "rounded-xl border border-stone-200 bg-[#F3F4F6]"}`}
          >
            <span className="hidden sm:inline-flex items-center shrink-0 max-w-[8.5rem] sm:max-w-[11.5rem] border-r border-stone-200 px-3 text-sm font-semibold text-stone-800">{allLabel}</span>
            <span className="flex-1 min-w-0 px-3 flex items-center text-sm text-stone-400 truncate">{placeholder}</span>
            {showMic ? <span className="w-10 shrink-0 inline-flex items-center justify-center text-stone-400" aria-hidden><Mic className="w-4 h-4" /></span> : null}
            {look === "quicksto" ? (
              <span className="w-12 shrink-0 inline-flex items-center justify-center" style={{ background: "var(--store-secondary)", color: "var(--store-primary)" }}><Search className="w-5 h-5" /></span>
            ) : (
              <span className="w-11 shrink-0 inline-flex items-center justify-center text-stone-800"><Search className="w-5 h-5" /></span>
            )}
          </button>
          <StorefrontSearchPopup
            open={open}
            onClose={() => setOpen(false)}
            shopHref={shopHref}
            navigate={navigate}
            look={look}
            testId={testId}
            placeholder={placeholder}
            categories={categories}
            showCategory
            allLabel={allLabel}
            showMic={showMic}
          />
        </>
      )}
    </>
  );
}

function HeaderSearchBar({ shopHref, navigate, testId = "store-search-bar", placeholder = "search what you love...", look = "classic" }) {
  return <StorefrontSearchTrigger shopHref={shopHref} navigate={navigate} testId={testId} placeholder={placeholder} look={look} />;
}

function HeaderIcons({
  showWallet, user, walletBal, base, showPhone, info, showWhats, showAccount, showCart,
  count, wishlist, openCart, logout, t, compact, showSearch, shopHref, navigate, look,
}) {
  const editorial = look === "perfecto" || look === "boxy" || look === "medico";
  const iconBtn = editorial
    ? `relative inline-flex items-center justify-center w-9 h-9 ${look === "boxy" || look === "medico" ? "text-[color:var(--store-primary)]" : "text-[#3E2723]"} hover:opacity-70`
    : "relative inline-flex items-center justify-center w-9 h-9 rounded-full text-stone-600 hover:text-stone-900 hover:bg-stone-100";
  return (
    <div className={`flex items-center ${compact ? "gap-1" : editorial ? "gap-1" : "gap-1.5 sm:gap-2"}`}>
      {showSearch ? <SearchToggle shopHref={shopHref} navigate={navigate} look={look} /> : null}
      {showWallet && (
        <Link
          to={user ? `${base}/account?tab=wallet` : `${base}/account/signup`}
          className="inline-flex items-center gap-1 text-xs sm:text-sm font-semibold px-2.5 h-9 rounded-full border border-stone-200 text-stone-700 hover:bg-stone-50"
          data-testid="store-wallet-chip"
        >
          <Wallet className="w-4 h-4" />
          <span className="hidden xs:inline">Wallet</span>
          {user && walletBal != null && <span className="font-mono-nums">₹{Number(walletBal).toLocaleString("en-IN")}</span>}
        </Link>
      )}
      {showPhone && (
        <a href={`tel:${info.phone}`} className="hidden lg:inline-flex items-center gap-1 text-xs text-stone-600 hover:text-stone-900 px-3 py-1.5 rounded-full border border-stone-200" data-testid="store-phone-link">
          <Phone className="w-3.5 h-3.5" /> {info.phone}
        </a>
      )}
      {showWhats && (
        <a href={`https://wa.me/${info.whatsapp.replace(/[^0-9]/g, "")}`} target="_blank" rel="noopener noreferrer" className={`${editorial ? "hidden md:inline-flex" : "hidden lg:inline-flex"} items-center gap-1 text-xs text-stone-600 hover:text-stone-900 px-3 py-1.5 rounded-full border border-stone-200`} data-testid="store-whatsapp">
          <MessageSquareText className="w-3.5 h-3.5" /> {t("store.whatsapp")}
        </a>
      )}
      {showAccount && (user ? (
        editorial ? (
          <Link to={`${base}/account`} className={iconBtn} data-testid="store-account" aria-label={t("store.account")}>
            <User className="w-5 h-5" strokeWidth={look === "boxy" ? 1.5 : 2} />
          </Link>
        ) : (
        <div className="hidden md:flex items-center gap-2">
          <Link to={`${base}/account`} className="text-sm text-stone-700 hover:text-stone-900" data-testid="store-account">{t("store.account")}</Link>
          <Link to={`${base}/account/orders`} className="text-sm text-stone-700 hover:text-stone-900" data-testid="store-account-orders">{t("store.orders")}</Link>
          <button onClick={async () => { await logout(); toast.success(t("common.signedOut")); }} data-testid="store-logout" className="text-stone-500 hover:text-stone-900"><LogOut className="w-4 h-4" /></button>
        </div>
        )
      ) : (
        <Link to={`${base}/account/login`} data-testid="store-login-link" className={editorial ? iconBtn : "hidden md:block"} aria-label={t("store.signIn")}>
          {editorial ? <User className="w-5 h-5" strokeWidth={look === "boxy" ? 1.5 : 2} /> : (
          <Button variant="ghost" size="sm" className="text-stone-700 hover:text-stone-900"><User className="w-4 h-4 mr-1" /> {t("store.signIn")}</Button>
          )}
        </Link>
      ))}
      {look !== "medico" ? (
      <Link to={`${base}/wishlist`} data-testid="store-wishlist-link" className={iconBtn}>
        <Heart className="w-5 h-5" />
        {wishlist?.count > 0 && <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-rose-600 text-white text-[10px] font-bold flex items-center justify-center font-mono-nums">{wishlist.count}</span>}
      </Link>
      ) : null}
      {showCart && (
        editorial ? (
          <button type="button" onClick={openCart} data-testid="store-cart-link" className={iconBtn}>
            <ShoppingCart className="w-5 h-5" strokeWidth={look === "boxy" ? 1.5 : 2} />
            {count > 0 && <span className={`absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full text-white text-[10px] font-bold ${look === "boxy" || look === "medico" ? "bg-[var(--store-primary)]" : "bg-[#3E2723]"}`}>{count}</span>}
          </button>
        ) : (
        <Button size="sm" onClick={openCart} data-testid="store-cart-link" className="btn-store-primary rounded-full relative h-9 px-3 sm:h-10 sm:px-4">
          <ShoppingCart className="w-4 h-4" />
          {count > 0 && <span className="ml-1.5 font-mono-nums text-xs font-bold">{count}</span>}
        </Button>
        )
      )}
    </div>
  );
}

function MobileMenuSheet({ menuOpen, setMenuOpen, menu, utility, base, showWallet, user, walletBal, wishlist, showAccount, logout, t, showSearch, shopHref, navigate, hideTrigger, look }) {
  return (
    <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
      {!hideTrigger ? (
        <SheetTrigger asChild>
          <Button variant="ghost" size="icon" className="md:hidden" data-testid="store-mobile-menu"><Menu className="w-5 h-5" /></Button>
        </SheetTrigger>
      ) : null}
      <SheetContent side="left" className="p-6">
        <div className="flex flex-col gap-4 mt-8">
          {showSearch ? (
            <StorefrontSearchTrigger shopHref={shopHref} navigate={navigate} look={look || "classic"} testId="m-store-search" placeholder="Search products" field />
          ) : null}
          {menu.map((m, i) => (
            <NavItem key={i} base={base} item={m} onClick={() => setMenuOpen(false)} className="text-lg font-display font-semibold text-stone-900" testid={`m-nav-${i}`} />
          ))}
          {utility.length > 0 && <div className="h-px bg-stone-200 my-2" />}
          {utility.map((m, i) => (
            <SmartLink key={`u-${i}`} base={base} item={m} onClick={() => setMenuOpen(false)} className="text-base text-stone-600" testid={`m-util-${i}`} />
          ))}
          <div className="h-px bg-stone-200 my-2" />
          {showWallet && (
            <Link to={user ? `${base}/account?tab=wallet` : `${base}/account/signup`} onClick={() => setMenuOpen(false)} className="text-base text-stone-700 inline-flex items-center gap-2" data-testid="m-store-wallet">
              <Wallet className="w-4 h-4" /> Wallet{user && walletBal != null ? ` · ₹${Number(walletBal).toLocaleString("en-IN")}` : ""}
            </Link>
          )}
          <Link to={`${base}/wishlist`} onClick={() => setMenuOpen(false)} className="text-base text-stone-700 inline-flex items-center gap-2" data-testid="m-store-wishlist"><Heart className="w-4 h-4" /> {t("store.wishlist")}{wishlist?.count > 0 ? ` (${wishlist.count})` : ""}</Link>
          {showAccount && (user ? (
            <>
              <Link to={`${base}/account`} onClick={() => setMenuOpen(false)} className="text-base text-stone-700" data-testid="m-store-account">{t("store.myAccount")}</Link>
              <Link to={`${base}/account/orders`} onClick={() => setMenuOpen(false)} className="text-base text-stone-700" data-testid="m-store-orders">{t("store.yourOrders")}</Link>
              <button onClick={async () => { await logout(); toast.success(t("common.signedOut")); setMenuOpen(false); }} className="text-base text-stone-700 text-left" data-testid="m-store-logout">{t("store.signOut")}</button>
            </>
          ) : (
            <>
              <Link to={`${base}/account/login`} onClick={() => setMenuOpen(false)} className="text-base text-stone-700" data-testid="m-store-login">{t("store.signIn")}</Link>
              <Link to={`${base}/account/signup`} onClick={() => setMenuOpen(false)} className="text-base text-stone-700" data-testid="m-store-signup">{t("store.createAccount")}</Link>
            </>
          ))}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function MixyMenuSheet({ menuOpen, setMenuOpen, menu, utility, base, includeMain, hideTrigger }) {
  const extra = (includeMain || utility.length === 0) ? menu : [];
  return (
    <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
      {!hideTrigger ? (
        <SheetTrigger asChild>
          <button
            type="button"
            data-testid="mixy-menu-btn"
            className="inline-flex items-center gap-2 h-10 px-3.5 rounded-lg bg-stone-950 text-white text-sm font-bold"
          >
            <Menu className="w-4 h-4" /> Menu
          </button>
        </SheetTrigger>
      ) : null}
      <SheetContent side="left" className="p-6">
        <div className="flex flex-col gap-3 mt-8" data-testid="mixy-utility-sheet">
          {utility.map((m, i) => (
            <SmartLink key={`u-${i}`} base={base} item={m} onClick={() => setMenuOpen(false)} className="text-base font-medium text-stone-800" testid={`m-util-${i}`} />
          ))}
          {utility.length > 0 && extra.length > 0 ? <div className="h-px bg-stone-200 my-2" /> : null}
          {extra.map((m, i) => (
            <NavItem key={i} base={base} item={m} onClick={() => setMenuOpen(false)} className="text-lg font-semibold text-stone-900" testid={`m-nav-${i}`} />
          ))}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function VariousMenuSheet({ menuOpen, setMenuOpen, menu, utility, base, includeMain, hideTrigger }) {
  const extra = (includeMain || utility.length === 0) ? menu : [];
  return (
    <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
      {!hideTrigger ? (
        <SheetTrigger asChild>
          <button
            type="button"
            data-testid="various-menu-btn"
            className="inline-flex items-center gap-2 h-10 px-3.5 rounded-lg text-white text-sm font-bold"
            style={{ background: "var(--store-primary, #1B4332)" }}
          >
            <Menu className="w-4 h-4" /> Menu
          </button>
        </SheetTrigger>
      ) : null}
      <SheetContent side="left" overlayClassName="z-[100]" className="z-[100] p-6">
        <div className="flex flex-col gap-3 mt-8" data-testid="various-utility-sheet">
          {utility.map((m, i) => (
            <SmartLink key={`u-${i}`} base={base} item={m} onClick={() => setMenuOpen(false)} className="text-base font-medium text-stone-800" testid={`m-util-${i}`} />
          ))}
          {utility.length > 0 && extra.length > 0 ? <div className="h-px bg-stone-200 my-2" /> : null}
          {extra.map((m, i) => (
            <NavItem key={i} base={base} item={m} onClick={() => setMenuOpen(false)} className="text-lg font-semibold text-stone-900" testid={`m-nav-${i}`} />
          ))}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function LocallyMenuSheet({ menuOpen, setMenuOpen, menu, utility, base, includeMain, hideTrigger }) {
  const extra = (includeMain || utility.length === 0) ? menu : [];
  return (
    <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
      {!hideTrigger ? (
        <SheetTrigger asChild>
          <button type="button" data-testid="locally-menu-btn" className="inline-flex items-center justify-center w-10 h-10 rounded-lg text-white" style={{ background: "var(--store-primary, #0F5C56)" }} aria-label="Menu">
            <Menu className="w-5 h-5" />
          </button>
        </SheetTrigger>
      ) : null}
      <SheetContent side="left" className="p-6">
        <div className="flex flex-col gap-3 mt-8" data-testid="locally-utility-sheet">
          {utility.map((m, i) => (
            <SmartLink key={`u-${i}`} base={base} item={m} onClick={() => setMenuOpen(false)} className="text-base font-medium text-stone-800" testid={`m-util-${i}`} />
          ))}
          {utility.length > 0 && extra.length > 0 ? <div className="h-px bg-stone-200 my-2" /> : null}
          {extra.map((m, i) => (
            <NavItem key={i} base={base} item={m} onClick={() => setMenuOpen(false)} className="text-lg font-semibold text-stone-900" testid={`m-nav-${i}`} />
          ))}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function QuickstoMenuSheet({ menuOpen, setMenuOpen, menu, utility, base, includeMain, hideTrigger }) {
  const extra = (includeMain || utility.length === 0) ? menu : [];
  return (
    <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
      {!hideTrigger ? (
        <SheetTrigger asChild>
          <button type="button" data-testid="quicksto-menu-btn" className="inline-flex items-center justify-center w-10 h-10 rounded-lg text-white bg-white/15" aria-label="Menu">
            <Menu className="w-5 h-5" />
          </button>
        </SheetTrigger>
      ) : null}
      <SheetContent side="left" className="p-6">
        <div className="flex flex-col gap-3 mt-8" data-testid="quicksto-utility-sheet">
          {utility.map((m, i) => (
            <SmartLink key={`u-${i}`} base={base} item={m} onClick={() => setMenuOpen(false)} className="text-base font-medium text-stone-800" testid={`m-util-${i}`} />
          ))}
          {utility.length > 0 && extra.length > 0 ? <div className="h-px bg-stone-200 my-2" /> : null}
          {extra.map((m, i) => (
            <NavItem key={i} base={base} item={m} onClick={() => setMenuOpen(false)} className="text-lg font-semibold text-stone-900" testid={`m-nav-${i}`} />
          ))}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function BeastMenuSheet({ menuOpen, setMenuOpen, menu, utility, base, includeMain, hideTrigger }) {
  const extra = (includeMain || utility.length === 0) ? menu : [];
  return (
    <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
      {!hideTrigger ? (
        <SheetTrigger asChild>
          <button type="button" data-testid="beast-menu-btn" className="inline-flex items-center justify-center w-10 h-10 text-white" aria-label="Menu">
            <Menu className="w-5 h-5" />
          </button>
        </SheetTrigger>
      ) : null}
      <SheetContent side="left" className="p-6 bg-black text-white border-white/10">
        <div className="flex flex-col gap-3 mt-8" data-testid="beast-utility-sheet">
          {utility.map((m, i) => (
            <SmartLink key={`u-${i}`} base={base} item={m} onClick={() => setMenuOpen(false)} className="text-base font-medium text-neutral-200" testid={`m-util-${i}`} />
          ))}
          {utility.length > 0 && extra.length > 0 ? <div className="h-px bg-white/10 my-2" /> : null}
          {extra.map((m, i) => (
            <NavItem key={i} base={base} item={m} onClick={() => setMenuOpen(false)} className="text-lg font-semibold text-white" testid={`m-nav-${i}`} />
          ))}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function useNewsletterSubmit() {
  const { slug } = useStoreBase();
  const [email, setEmail] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    const v = email.trim();
    if (!v) return;
    try {
      await api.post(`/store/${slug}/newsletter`, { email: v });
      toast.success("Thanks for subscribing");
      setEmail("");
    } catch (err) {
      toast.error(extractErr(err));
    }
  };
  return { email, setEmail, submit };
}

function BeastNewsletter() {
  const { email, setEmail, submit } = useNewsletterSubmit();
  return (
    <form
      data-testid="beast-newsletter"
      onSubmit={submit}
      className="relative w-full max-w-sm"
    >
      <input
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="Enter your email..."
        data-testid="beast-newsletter-input"
        className="w-full h-11 rounded-full border border-white/70 bg-transparent pl-4 pr-12 text-sm text-white placeholder:text-neutral-500 outline-none"
      />
      <button type="submit" data-testid="beast-newsletter-submit" className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full text-white flex items-center justify-center" aria-label="Subscribe">
        <Send className="w-4 h-4" />
      </button>
    </form>
  );
}

function VariousNewsletter() {
  const { email, setEmail, submit } = useNewsletterSubmit();
  return (
    <form
      data-testid="various-newsletter"
      onSubmit={submit}
      className="flex items-stretch gap-0 w-full max-w-md rounded-lg overflow-hidden border bg-white"
      style={{ borderColor: "color-mix(in srgb, var(--store-primary, #1B4332) 15%, transparent)" }}
    >
      <input
        type="email"
        required
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="Your email"
        className="flex-1 px-3 py-2.5 text-sm outline-none"
        data-testid="various-newsletter-input"
      />
      <button type="submit" data-testid="various-newsletter-submit" className="px-4 text-sm font-bold text-white" style={{ background: "var(--store-primary, #1B4332)" }}>
        Subscribe
      </button>
    </form>
  );
}

function LocallyNewsletter({ primary, secondary }) {
  const { email, setEmail, submit } = useNewsletterSubmit();
  return (
    <form
      data-testid="locally-newsletter"
      onSubmit={submit}
      className="flex items-stretch gap-0 w-full max-w-md"
    >
      <input
        type="email"
        required
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="Enter your email address"
        className="flex-1 h-11 px-4 rounded-l-full text-sm outline-none text-stone-800"
        data-testid="locally-newsletter-input"
      />
      <button type="submit" data-testid="locally-newsletter-submit" className="h-11 px-5 rounded-r-full text-sm font-bold" style={{ background: secondary, color: primary }}>
        Subscribe
      </button>
    </form>
  );
}

function QuickstoNewsletter({ primary, secondary }) {
  const { email, setEmail, submit } = useNewsletterSubmit();
  return (
    <form
      data-testid="quicksto-newsletter"
      onSubmit={submit}
      className="flex items-stretch gap-0 w-full max-w-md"
    >
      <input
        type="email"
        required
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="Enter your email address"
        className="flex-1 h-11 px-4 rounded-l-full text-sm outline-none text-stone-800"
        data-testid="quicksto-newsletter-input"
      />
      <button type="submit" data-testid="quicksto-newsletter-submit" className="h-11 px-5 rounded-r-full text-sm font-bold" style={{ background: secondary, color: primary }}>
        Subscribe
      </button>
    </form>
  );
}

function BigShowNewsletter() {
  const { email, setEmail, submit } = useNewsletterSubmit();
  return (
    <form
      data-testid="bigshow-newsletter"
      onSubmit={submit}
      className="flex items-center gap-2 w-full max-w-md"
    >
      <input
        type="email"
        required
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="Email address"
        className="flex-1 h-12 px-5 rounded-full text-sm outline-none bg-white/10 text-white placeholder:text-white/45 border border-white/15"
        data-testid="bigshow-newsletter-input"
      />
      <button type="submit" data-testid="bigshow-newsletter-submit" className="w-12 h-12 rounded-full bg-white text-stone-900 inline-flex items-center justify-center shrink-0" aria-label="Subscribe">
        <ArrowRight className="w-5 h-5" />
      </button>
    </form>
  );
}

function FurnisNewsletter() {
  const { email, setEmail, submit } = useNewsletterSubmit();
  return (
    <form
      data-testid="furnis-newsletter"
      onSubmit={submit}
      className="w-full max-w-sm"
    >
      <div className="flex items-center border-b border-stone-300">
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Email"
          className="flex-1 h-10 bg-transparent text-sm outline-none text-stone-800 placeholder:text-stone-400"
          data-testid="furnis-newsletter-input"
        />
        <button type="submit" data-testid="furnis-newsletter-submit" className="w-9 h-9 inline-flex items-center justify-center text-stone-700" aria-label="Subscribe">
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </form>
  );
}

function BoxyNewsletter() {
  const { email, setEmail, submit } = useNewsletterSubmit();
  return (
    <form
      data-testid="boxy-newsletter"
      onSubmit={submit}
    >
      <div className="flex items-end gap-2 border-b border-white/80 pb-1.5">
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Enter email here"
          className="flex-1 bg-transparent text-sm text-white placeholder:text-white/60 outline-none py-1"
          data-testid="boxy-newsletter-input"
        />
        <button type="submit" aria-label="Subscribe" data-testid="boxy-newsletter-submit" className="text-white pb-0.5">
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </form>
  );
}

function SocialRow({ socials, dark, mint }) {
  const cls = mint
    ? "w-9 h-9 rounded-full bg-[var(--store-primary)] text-white flex items-center justify-center hover:opacity-90"
    : dark
    ? "w-9 h-9 rounded-full border border-white/15 flex items-center justify-center text-white/80 hover:text-white hover:border-white/40"
    : "w-8 h-8 rounded-full border border-stone-200 flex items-center justify-center text-stone-600 hover:text-stone-900 hover:border-stone-400";
  return (
    <div className="flex items-center gap-3 mt-4">
      {socials.instagram && <a href={socials.instagram} target="_blank" rel="noopener noreferrer" className={cls} data-testid="footer-social-instagram" aria-label="Instagram"><Instagram className="w-4 h-4" /></a>}
      {socials.facebook && <a href={socials.facebook} target="_blank" rel="noopener noreferrer" className={cls} data-testid="footer-social-facebook" aria-label="Facebook"><Facebook className="w-4 h-4" /></a>}
      {socials.youtube && <a href={socials.youtube} target="_blank" rel="noopener noreferrer" className={cls} data-testid="footer-social-youtube" aria-label="YouTube"><Youtube className="w-4 h-4" /></a>}
      {socials.twitter && <a href={socials.twitter} target="_blank" rel="noopener noreferrer" className={cls} data-testid="footer-social-twitter" aria-label="X"><Twitter className="w-4 h-4" /></a>}
      {socials.threads && <a href={socials.threads} target="_blank" rel="noopener noreferrer" className={cls} data-testid="footer-social-threads" aria-label="Threads"><MessageSquareText className="w-4 h-4" /></a>}
    </div>
  );
}

function PoweredByCredit({ config, className, nameClass }) {
  const on = Boolean(config?.copyright_removal_active);
  const settings = on ? (config?.copyright_removal || {}) : {};
  if (on && settings.hide) return null;
  const custom = String(settings.label || "").trim();
  const name = custom || "MeraStartup";
  return (
    <div className={className} data-testid="store-powered-by">
      Powered by <span className={nameClass}>{name}</span>
    </div>
  );
}

export default function StorefrontLayout({ config, children }) {
  const { slug, base } = useStoreBase();
  const location = useLocation();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { count } = useCart();
  const { openCart, cartOpen } = useCartUI();
  const wishlist = useWishlist();
  const { user, logout } = useCustomerAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const [bigshowScrolled, setBigshowScrolled] = useState(false);
  const [furnisScrolled, setFurnisScrolled] = useState(false);
  const modern = isModernStorefront(config);
  const trendy = isTrendyStorefront(config);
  const perfecto = isPerfectoStorefront(config);
  const boxy = isBoxyStorefront(config);
  const medico = isMedicoStorefront(config);
  const mixy = isMixyStorefront(config);
  const various = isVariousStorefront(config);
  const beast = isBeastStorefront(config);
  const locally = isLocallyStorefront(config);
  const quicksto = isQuickstoStorefront(config);
  const bigshow = isBigshowStorefront(config);
  const furnis = isFurnisStorefront(config);
  const carparts = isCarPartsStorefront(config);
  const cosmos = isCosmosStorefront(config);
  const ondemand = isOndemandStorefront(config);
  const zeploOnClassic = zeploFeaturesOn(config) && !ondemand;
  const [cosmosScrolled, setCosmosScrolled] = useState(false);
  const overlayHomePath = String(base || "").replace(/\/+$/, "") || "";
  const overlayPath = String(location.pathname || "/").replace(/\/+$/, "") || "/";
  const overlayIsHome = overlayHomePath ? overlayPath === overlayHomePath : overlayPath === "/";
  const bigshowHomePath = overlayHomePath;
  const bigshowPath = overlayPath;
  const bigshowHome = bigshow && overlayIsHome;
  const furnisHome = furnis && overlayIsHome;
  const cosmosHome = cosmos && overlayIsHome;
  const brand = resolveStoreColors(config);
  const primary = brand.primary;
  const secondary = brand.secondary;
  const mixyAccent = (config.branding?.accent_color || "").trim() || (mixy ? "#FFDC2E" : secondary);
  const themeId = storefrontThemeId(config);
  const [catNav, setCatNav] = useState([]);

  const googleFont = config.preferences?.google_font || null;
  useEffect(() => {
    if (!googleFont) return;
    const id = `gf-${googleFont.replace(/\s+/g, "-")}`;
    if (!document.getElementById(id)) {
      const link = document.createElement("link");
      link.id = id;
      link.rel = "stylesheet";
      link.href = googleFontHref(googleFont);
      document.head.appendChild(link);
    }
  }, [googleFont]);
  useEffect(() => {
    if (!bigshow || !bigshowHome) {
      setBigshowScrolled(false);
      return undefined;
    }
    const onScroll = () => setBigshowScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [bigshow, bigshowHome]);
  useEffect(() => {
    if (!furnis || !furnisHome) {
      setFurnisScrolled(false);
      return undefined;
    }
    const onScroll = () => setFurnisScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [furnis, furnisHome]);
  useEffect(() => {
    if (!cosmos || !cosmosHome) {
      setCosmosScrolled(false);
      return undefined;
    }
    const onScroll = () => setCosmosScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [cosmos, cosmosHome]);
  const fontStyle = googleFont ? { fontFamily: fontStack(googleFont) } : undefined;

  const header = config.header || {};
  const footer = config.footer || {};
  const info = config.store_info || {};
  const clampLogo = (v, fallback) => {
    const n = parseInt(v, 10);
    if (!Number.isFinite(n)) return fallback;
    return Math.max(24, Math.min(96, n));
  };
  const logoHMobile = clampLogo(info.logo_height_mobile, 48);
  const logoHDesktop = clampLogo(info.logo_height_desktop, 64);
  const ann = header.announcement || {};
  const contact = footer.contact || {};
  const socials = footer.socials || {};
  const menu = (() => {
    const raw = header.menu || [];
    if (!config.store_blog_active) return raw;
    const hasBlog = raw.some((m) => {
      const p = String(m?.path || "").replace(/^\//, "").replace(/\/+$/, "");
      return p === "blog" || p.endsWith("/blog");
    });
    if (hasBlog) return raw;
    return [...raw, { label: "Blog", path: "blog" }];
  })();
  const utility = header.utility_menu || [];
  const columns = footer.columns || [];

  const showWhats = header.show_whatsapp !== false && info.whatsapp;
  const showPhone = header.show_phone === true && info.phone;
  const showAccount = header.show_account !== false;
  const showCart = header.show_cart !== false;
  const showSearch = header.show_search !== false;
  const sticky = header.sticky !== false;
  const gtmId = /^[A-Za-z0-9._-]{2,128}$/.test(String(config.ads?.gtm_id || "").trim()) ? String(config.ads.gtm_id).trim() : "";
  const googleI18n = Boolean(config.i18n?.google_storefront) && (config.i18n?.google_storefront_language || "en") !== "en";
  const showWallet = Boolean(config.customer_refer_active);
  const [walletBal, setWalletBal] = useState(null);

  useEffect(() => {
    const q = new URLSearchParams(window.location.search).get("ref");
    if (q) try { localStorage.setItem(`ref:${slug}`, q); } catch { /* ignore */ }
  }, [slug]);

  useEffect(() => {
    if (!showWallet || !user) { setWalletBal(null); return; }
    api.get(`/store/${slug}/wallet`).then(({ data }) => setWalletBal(data.balance)).catch(() => setWalletBal(null));
  }, [showWallet, user, slug]);

  useEffect(() => {
    if ((!trendy && !perfecto && !boxy && !mixy && !various && !locally && !quicksto && !carparts && !ondemand) || !slug) return;
    api.get(`/store/${slug}/categories`).then(({ data }) => {
      const rows = Array.isArray(data) ? data : [];
      if (mixy || various || locally || quicksto || carparts || ondemand) {
        setCatNav(rows);
        return;
      }
      setCatNav(rows.filter((c) => !c.parent_id).slice(0, 10));
    }).catch(() => setCatNav([]));
  }, [trendy, perfecto, boxy, mixy, various, locally, quicksto, carparts, ondemand, slug]);

  useEffect(() => {
    if ((!perfecto && !boxy && !furnis && !cosmos) || googleFont) return;
    const id = cosmos ? "gf-inter-cosmos" : perfecto ? "gf-playfair-perfecto" : boxy ? "gf-playfair-boxy" : "gf-cormorant-furnis";
    if (document.getElementById(id)) return;
    const link = document.createElement("link");
    link.id = id;
    link.rel = "stylesheet";
    link.href = cosmos
      ? "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap"
      : furnis
      ? "https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,500;0,600;1,500;1,600&family=Playfair+Display:ital,wght@0,500;1,500&display=swap"
      : "https://fonts.googleapis.com/css2?family=Playfair+Display:wght@500;600;700&display=swap";
    document.head.appendChild(link);
  }, [perfecto, boxy, furnis, cosmos, googleFont]);

  const shopHref = `${base}/shop`;
  const iconProps = {
    showWallet, user, walletBal, base, showPhone, info, showWhats, showAccount, showCart,
    count, wishlist, openCart, logout, t, showSearch, shopHref, navigate, look: themeId,
  };
  const menuSheetProps = {
    menuOpen, setMenuOpen, menu, utility, base, showWallet, user, walletBal, wishlist, showAccount, logout, t, showSearch, shopHref, navigate, look: themeId,
  };
  const brandInfo = { ...info, base };

  return (
    <div className={`${googleFont ? "sf-fontroot" : ""} overflow-x-clip w-full max-w-[100vw]`} style={{ ...fontStyle, ...(mixy ? { "--mixy-accent": mixyAccent } : {}) }} data-storefront-theme={themeId}>
      {googleI18n ? <GoogleStorefrontTranslate language={config.i18n.google_storefront_language} /> : null}
      <StoreHead
        seo={config.seo}
        ads={config.ads}
        storeInfo={config.store_info}
        branding={config.branding}
        noIndex={typeof window !== "undefined" && isPlatformHostname(window.location.hostname)}
      />
      <StoreAds ads={config.ads} />
      <StorefrontFingerprint />
      {gtmId ? (
        <noscript>
          <iframe title="Google Tag Manager" src={`https://www.googletagmanager.com/ns.html?id=${gtmId}`} height="0" width="0" style={{ display: "none", visibility: "hidden" }} />
        </noscript>
      ) : null}
      {googleFont && (
        <style>{`.sf-fontroot, .sf-fontroot .font-display { font-family: ${fontStack(googleFont)} !important; }`}</style>
      )}
      {config.under_construction?.enabled && config.viewer_is_merchant ? (
        <div className="bg-amber-500 text-amber-950 text-sm font-semibold text-center py-2 px-4" data-testid="uc-admin-preview-banner">
          Under construction is live for visitors. You are viewing the store as admin.
        </div>
      ) : null}
      <AnnouncementBar ann={ann} modern={modern} base={base} />

      {utility.length > 0 && !perfecto && !boxy && !mixy && !various && !beast && !locally && !quicksto && !bigshow && !furnis && !carparts && !cosmos && !ondemand && (
        <div className={`hidden md:block ${modern ? "bg-neutral-950 text-white/70 border-b border-white/10" : "bg-stone-50 border-b border-stone-100"}`}>
          <div className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-end gap-5 h-8 text-xs ${modern ? "text-white/60" : "text-stone-500"}`}>
            {utility.map((m, i) => (
              <SmartLink key={i} base={base} item={m} className={modern ? "hover:text-white" : "hover:text-stone-900"} testid={`util-nav-${i}`} />
            ))}
          </div>
        </div>
      )}

      {ondemand ? (
        <div data-testid="store-header-ondemand">
        <header className={`${sticky ? "md:sticky md:top-0" : "relative"} ${cartOpen ? "z-30" : "z-[80]"} w-full bg-white border-b border-stone-100`}>
          <style>{`
            .store-logo-img { height: ${logoHMobile}px; width: auto; max-width: min(48vw, 160px); object-fit: contain; }
            @media (min-width: 768px) {
              .store-logo-img { height: ${logoHDesktop}px; max-width: min(24vw, 180px); }
            }
          `}</style>
          <MobileMenuSheet {...menuSheetProps} hideTrigger look="ondemand" />
          <div className="md:hidden px-3 min-h-14 py-2 flex items-center gap-2" data-testid="ondemand-header-mobile">
            <BrandMark info={brandInfo} primary={primary} imgClass="store-logo-img" wrapClass="flex items-center gap-2 shrink-0" nameClass="font-extrabold tracking-tight truncate max-w-[100px]" />
            <OndemandPinChip slug={slug} onChange={() => window.dispatchEvent(new Event("ondemand-pin-edit"))} />
            <div className="ml-auto flex items-center gap-0.5 shrink-0" data-testid="ondemand-header-icons">
              {showAccount ? (
                <Link to={user ? `${base}/account` : `${base}/account/login`} className="w-9 h-9 inline-flex items-center justify-center" data-testid="store-login-link" aria-label={t("store.account")}>
                  <User className="w-4 h-4" />
                </Link>
              ) : null}
              {showCart ? (
                <button type="button" onClick={openCart} data-testid="store-cart-link" className="relative w-9 h-9 inline-flex items-center justify-center">
                  <ShoppingBag className="w-4 h-4" />
                  {count > 0 && <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full text-[10px] font-bold text-white" style={{ background: primary }}>{count}</span>}
                </button>
              ) : null}
            </div>
          </div>
          <div className="md:hidden px-3 pb-2">
            {showSearch ? (
              <StorefrontSearchTrigger shopHref={shopHref} navigate={navigate} look="ondemand" testId="ondemand-search-bar-mobile" placeholder='Search for "kurkure"' field className="flex-1 min-w-0 h-11 rounded-full border border-stone-200 bg-stone-50 px-4 inline-flex items-center gap-3 text-left text-stone-400 w-full" />
            ) : null}
          </div>
          <div className="hidden md:flex max-w-[90rem] mx-auto px-4 sm:px-6 lg:px-8 min-h-[4.25rem] py-2 items-center gap-3" data-testid="ondemand-header-row">
            <BrandMark info={brandInfo} primary={primary} imgClass="store-logo-img" wrapClass="flex items-center gap-2 shrink-0" nameClass="font-extrabold text-xl tracking-tight lowercase" />
            <OndemandPinChip slug={slug} onChange={() => window.dispatchEvent(new Event("ondemand-pin-edit"))} />
            {showSearch ? (
              <StorefrontSearchTrigger shopHref={shopHref} navigate={navigate} look="ondemand" testId="ondemand-search-bar" placeholder='Search for "kurkure"' field className="flex-1 min-w-0 h-11 rounded-full border border-stone-200 bg-stone-50 px-4 inline-flex items-center gap-3 text-left text-stone-400" />
            ) : <div className="flex-1" />}
            <div className="flex items-center gap-1 shrink-0" data-testid="ondemand-header-icons-desktop">
              {showAccount ? (
                <Link to={user ? `${base}/account` : `${base}/account/login`} className="inline-flex flex-col items-center justify-center w-12 h-12 text-stone-800" data-testid="store-account" aria-label={t("store.account")}>
                  <User className="w-5 h-5" />
                  <span className="text-[10px] font-semibold">{user ? "Account" : "Login"}</span>
                </Link>
              ) : null}
              <Link to={`${base}/wishlist`} data-testid="store-wishlist-link" className="relative inline-flex flex-col items-center justify-center w-12 h-12 text-stone-800">
                <Heart className="w-5 h-5" />
                <span className="text-[10px] font-semibold">Wishlist</span>
                {wishlist?.count > 0 && <span className="absolute top-1 right-1 min-w-[14px] h-3.5 px-0.5 rounded-full text-[9px] font-bold text-white" style={{ background: primary }}>{wishlist.count}</span>}
              </Link>
              {showCart ? (
                <button type="button" onClick={openCart} data-testid="store-cart-link" className="relative inline-flex flex-col items-center justify-center w-12 h-12 text-stone-800">
                  <ShoppingBag className="w-5 h-5" />
                  <span className="text-[10px] font-semibold">Cart</span>
                  {count > 0 && <span className="absolute top-1 right-1 min-w-[14px] h-3.5 px-0.5 rounded-full text-[9px] font-bold text-white" style={{ background: primary }}>{count}</span>}
                </button>
              ) : null}
            </div>
          </div>
          <div className="hidden md:block">
            <OndemandCatMenu items={catNav} base={base} menu={menu} />
          </div>
        </header>
        <div className={`md:hidden bg-white border-b border-stone-100 ${sticky ? "sticky top-0" : ""} ${cartOpen ? "z-30" : "z-[80]"}`}>
          <OndemandCatMenu items={catNav} base={base} menu={menu} />
        </div>
        </div>
      ) : cosmos ? (
        <header
          className={`${cosmosHome && !cosmosScrolled ? "relative z-50 h-0 overflow-visible" : "sticky top-0 z-50 bg-white border-b border-stone-100"} w-full`}
          data-testid="store-header-cosmos"
          data-cosmos-home={cosmosHome ? "true" : "false"}
          data-cosmos-scrolled={cosmosHome && cosmosScrolled ? "true" : "false"}
        >
          <style>{`
            .store-logo-img { height: ${logoHMobile}px; width: auto; max-width: min(48vw, 180px); object-fit: contain; }
            @media (min-width: 768px) {
              .store-logo-img { height: ${logoHDesktop}px; max-width: min(28vw, 220px); }
            }
          `}</style>
          <MobileMenuSheet {...menuSheetProps} hideTrigger look="cosmos" />
          {(() => {
            const overlay = cosmosHome && !cosmosScrolled;
            const ink = "text-stone-900";
            const logoSrc = info.logo_white_bg_url || info.logo_url;
            const iconBtn = `inline-flex items-center justify-center ${ink} hover:bg-stone-100`;
            const utilCta = utility[0];
            return (
          <div className={`cosmos-header-bar ${cosmosHome && overlay ? "pt-2" : "py-1"} px-3 sm:px-5`} data-testid="cosmos-header-bar">
            <div className="max-w-[90rem] mx-auto">
              <div className="md:hidden px-1 min-h-14 py-2 grid grid-cols-[1fr_auto_1fr] items-center gap-2" data-testid="cosmos-header-mobile">
                <button type="button" data-testid="cosmos-menu-btn" onClick={() => setMenuOpen(true)} className={`justify-self-start w-10 h-10 ${iconBtn}`} aria-label="Menu">
                  <Menu className="w-5 h-5" />
                </button>
                <BrandMark info={brandInfo} primary={primary} logoUrl={logoSrc} imgClass="store-logo-img" wrapClass="flex items-center justify-center gap-2" nameClass={`font-extrabold tracking-tight truncate max-w-[140px] ${ink}`} />
                <div className="justify-self-end flex items-center gap-0.5" data-testid="cosmos-header-icons">
                  {showSearch ? <SearchToggle shopHref={shopHref} navigate={navigate} look="cosmos" testId="store-search-toggle-mobile" className={`w-9 h-9 ${iconBtn}`} /> : null}
                  {showAccount ? (
                    <Link to={user ? `${base}/account` : `${base}/account/login`} className={`w-9 h-9 ${iconBtn}`} data-testid="store-login-link" aria-label={t("store.account")}>
                      <User className="w-4 h-4" />
                    </Link>
                  ) : null}
                  {showCart ? (
                    <button type="button" onClick={openCart} data-testid="store-cart-link" className={`relative w-9 h-9 ${iconBtn}`}>
                      <ShoppingBag className="w-4 h-4" />
                      <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full text-[10px] font-bold text-white" style={{ background: primary }}>{count}</span>
                    </button>
                  ) : null}
                </div>
              </div>
              <div className="hidden md:grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] max-w-[90rem] mx-auto px-4 lg:px-8 min-h-[4.25rem] py-2 items-center gap-3" data-testid="cosmos-header-row">
                <CosmosMenuRow menu={menu} base={base} onOpenMenu={() => setMenuOpen(true)} />
                <BrandMark info={brandInfo} primary={primary} logoUrl={logoSrc} imgClass="store-logo-img" wrapClass="flex items-center justify-center gap-2 px-2" nameClass={`font-extrabold text-xl tracking-tight uppercase ${ink}`} />
                <div className="flex justify-end items-center gap-1.5" data-testid="cosmos-header-icons-desktop">
                  {utilCta ? (
                    <SmartLink
                      base={base}
                      item={utilCta}
                      testid="cosmos-header-cta"
                      className="hidden lg:inline-flex items-center h-9 px-4 rounded-full text-sm font-semibold text-white shrink-0"
                      style={{ background: primary }}
                    />
                  ) : null}
                  {showSearch ? <SearchToggle shopHref={shopHref} navigate={navigate} look="cosmos" className={`w-10 h-10 ${iconBtn}`} /> : null}
                  {showAccount ? (
                    <Link to={user ? `${base}/account` : `${base}/account/login`} className={`w-10 h-10 ${iconBtn}`} data-testid="store-account" aria-label={t("store.account")}>
                      <User className="w-5 h-5" />
                    </Link>
                  ) : null}
                  <Link to={`${base}/wishlist`} data-testid="store-wishlist-link" className={`relative w-10 h-10 ${iconBtn}`}>
                    <Heart className="w-5 h-5" />
                    {wishlist?.count > 0 && <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full text-[10px] font-bold text-white" style={{ background: primary }}>{wishlist.count}</span>}
                  </Link>
                  {showCart ? (
                    <button type="button" onClick={openCart} data-testid="store-cart-link" className={`relative w-10 h-10 ${iconBtn}`}>
                      <ShoppingBag className="w-5 h-5" />
                      <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full text-[10px] font-bold text-white" style={{ background: primary }}>{count}</span>
                    </button>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
            );
          })()}
        </header>
      ) : carparts ? (
        <header className={`${sticky ? "sticky top-0 z-[80]" : "relative z-[80]"} w-full bg-white border-b border-stone-100`} data-testid="store-header-carparts">
          <style>{`
            .store-logo-img { height: ${logoHMobile}px; width: auto; max-width: min(48vw, 180px); object-fit: contain; }
            @media (min-width: 768px) {
              .store-logo-img { height: ${logoHDesktop}px; max-width: min(28vw, 220px); }
            }
          `}</style>
          <MobileMenuSheet {...menuSheetProps} hideTrigger look="carparts" />
          <div className="md:hidden px-3 min-h-14 py-2 flex items-center gap-2" data-testid="carparts-header-mobile">
            <button type="button" data-testid="carparts-menu-btn" onClick={() => setMenuOpen(true)} className="inline-flex items-center justify-center w-10 h-10 text-stone-800" aria-label="Menu">
              <Menu className="w-5 h-5" />
            </button>
            <div className="flex-1 min-w-0 flex justify-center">
              <BrandMark info={brandInfo} primary={primary} imgClass="store-logo-img" wrapClass="flex items-center justify-center gap-2" nameClass="font-extrabold text-stone-900 truncate max-w-[120px]" />
            </div>
            <div className="flex items-center gap-0.5 shrink-0" data-testid="carparts-header-icons">
              <Link to={`${base}/wishlist`} data-testid="store-wishlist-link" className="relative inline-flex items-center justify-center w-9 h-9 text-stone-800">
                <Heart className="w-5 h-5" />
                {wishlist?.count > 0 && <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full text-white text-[10px] font-bold" style={{ background: primary }}>{wishlist.count}</span>}
              </Link>
              {showCart ? (
                <button type="button" onClick={openCart} data-testid="store-cart-link" className="relative inline-flex items-center justify-center w-9 h-9 text-stone-800">
                  <ShoppingBag className="w-5 h-5" />
                  <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full text-white text-[10px] font-bold" style={{ background: secondary }}>{count}</span>
                </button>
              ) : null}
            </div>
          </div>
          {showSearch ? (
            <div className="md:hidden px-3 pb-2 flex items-center gap-2">
              <CarPartsShopByCats categories={catNav} base={base} />
              <StorefrontSearchTrigger shopHref={shopHref} navigate={navigate} look="carparts" testId="carparts-search-bar-mobile" placeholder="Search for products, categories..." field className="flex-1 min-w-0 h-11 rounded-full border border-stone-200 bg-stone-50 px-4 inline-flex items-center gap-3 text-left text-stone-400" />
            </div>
          ) : (
            <div className="md:hidden px-3 pb-2"><CarPartsShopByCats categories={catNav} base={base} /></div>
          )}
          <div className="hidden md:flex max-w-[90rem] mx-auto px-6 lg:px-10 min-h-[4.5rem] py-2 items-center gap-3" data-testid="carparts-header-row">
            <BrandMark info={brandInfo} primary={primary} imgClass="store-logo-img" wrapClass="flex items-center gap-2 shrink-0" nameClass="font-extrabold text-xl" />
            <CarPartsShopByCats categories={catNav} base={base} />
            {showSearch ? (
              <StorefrontSearchTrigger shopHref={shopHref} navigate={navigate} look="carparts" testId="carparts-search-bar" placeholder="Search for products, categories..." field className="flex-1 min-w-0 h-11 rounded-full border border-stone-200 bg-stone-50 px-4 inline-flex items-center gap-3 text-left text-stone-400" />
            ) : <div className="flex-1" />}
            <div className="flex items-center gap-2 shrink-0" data-testid="carparts-header-icons-desktop">
              {showAccount ? (
                <Link to={user ? `${base}/account` : `${base}/account/login`} className="inline-flex items-center gap-1.5 text-stone-700" data-testid={user ? "store-account" : "store-login-link"} aria-label={t("store.account")}>
                  <User className="w-5 h-5" />
                  <span className="hidden xl:inline text-xs font-semibold">{user ? t("store.account") : "Login / Register"}</span>
                </Link>
              ) : null}
              <Link to={`${base}/wishlist`} data-testid="store-wishlist-link" className="relative inline-flex items-center justify-center w-9 h-9 text-stone-700">
                <Heart className="w-5 h-5" />
                {wishlist?.count > 0 && <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full text-white text-[10px] font-bold" style={{ background: primary }}>{wishlist.count}</span>}
              </Link>
              {showCart ? (
                <button type="button" onClick={openCart} data-testid="store-cart-link" className="relative inline-flex items-center justify-center w-9 h-9 text-stone-700">
                  <ShoppingBag className="w-5 h-5" />
                  <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full text-white text-[10px] font-bold" style={{ background: secondary }}>{count}</span>
                </button>
              ) : null}
            </div>
          </div>
          <div className="hidden md:flex max-w-[90rem] mx-auto px-6 lg:px-10 min-h-[3.75rem] py-3.5 items-center justify-between gap-4 border-t border-stone-100" data-testid="carparts-menu-row">
            <nav className="flex items-center gap-5 lg:gap-7 text-[13px] font-extrabold tracking-[0.08em] uppercase text-stone-800 min-w-0 flex-wrap overflow-visible relative z-[90]">
              {menu.map((m, i) => (
                <NavItem key={i} base={base} item={m} desktop className="whitespace-nowrap hover:opacity-70" testid={`store-nav-${i}`} />
              ))}
            </nav>
            <div className="flex items-center gap-3 shrink-0" data-testid="carparts-header-contact">
              {showWhats && info.whatsapp ? (
                <a href={`https://wa.me/${info.whatsapp.replace(/[^0-9]/g, "")}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center w-9 h-9 rounded-full text-white" style={{ background: "#25D366" }} data-testid="store-whatsapp" aria-label="WhatsApp">
                  <MessageSquareText className="w-4 h-4" />
                </a>
              ) : null}
              {info.phone ? (
                <a href={`tel:${info.phone}`} className="inline-flex items-center gap-2 text-stone-700" data-testid="store-phone-link" aria-label="Phone">
                  <span className="inline-flex items-center justify-center w-9 h-9 rounded-full text-white" style={{ background: secondary }}>
                    <Phone className="w-4 h-4" />
                  </span>
                  <span className="hidden lg:inline text-xs font-semibold">Call our product expert: <span className="font-extrabold">{info.phone}</span></span>
                </a>
              ) : null}
            </div>
          </div>
        </header>
      ) : furnis ? (
        <header
          className={`${furnisHome && !furnisScrolled ? "relative z-50 h-0 overflow-visible" : "sticky top-0 z-50 bg-white border-b border-stone-200/80"} w-full`}
          data-testid="store-header-furnis"
          data-furnis-home={furnisHome ? "true" : "false"}
          data-furnis-scrolled={furnisHome && furnisScrolled ? "true" : "false"}
        >
          <style>{`
            .store-logo-img { height: ${logoHMobile}px; width: auto; max-width: min(48vw, 180px); object-fit: contain; }
            @media (min-width: 768px) {
              .store-logo-img { height: ${logoHDesktop}px; max-width: min(28vw, 220px); }
            }
          `}</style>
          <MobileMenuSheet {...menuSheetProps} hideTrigger look="furnis" />
          {(() => {
            const overlay = furnisHome && !furnisScrolled;
            const ink = overlay ? "text-white" : "text-stone-900";
            const logoSrc = overlay ? info.logo_url : (info.logo_white_bg_url || info.logo_url);
            const iconBtn = `inline-flex items-center justify-center ${ink} ${overlay ? "hover:bg-white/10" : "hover:bg-stone-100"}`;
            return (
          <div className={`${overlay ? "furnis-header-overlay pointer-events-none" : ""} ${furnisHome && overlay ? "pt-2" : "py-1"} px-3 sm:px-5`}>
            <div className="pointer-events-auto max-w-[90rem] mx-auto">
              <div className="md:hidden px-1 min-h-14 py-2 grid grid-cols-[1fr_auto_1fr] items-center gap-2" data-testid="furnis-header-mobile">
                <button type="button" data-testid="furnis-menu-btn" onClick={() => setMenuOpen(true)} className={`justify-self-start w-10 h-10 ${iconBtn}`} aria-label="Menu">
                  <Menu className="w-5 h-5" />
                </button>
                <BrandMark info={brandInfo} primary={primary} logoUrl={logoSrc} imgClass="store-logo-img" wrapClass="flex items-center justify-center gap-2" nameClass={`font-display tracking-[0.18em] uppercase truncate max-w-[140px] ${ink}`} />
                <div className="justify-self-end flex items-center gap-0.5" data-testid="furnis-header-icons">
                  {showSearch ? <SearchToggle shopHref={shopHref} navigate={navigate} look="furnis" testId="store-search-toggle-mobile" className={`w-9 h-9 ${iconBtn}`} /> : null}
                  {showCart ? (
                    <button type="button" onClick={openCart} data-testid="store-cart-link" className={`relative w-9 h-9 ${iconBtn}`}>
                      <ShoppingBag className="w-4 h-4" />
                      {count > 0 && <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full text-[10px] font-bold" style={{ background: overlay ? "#ffffff" : primary, color: overlay ? "#1C1917" : "#ffffff" }}>{count}</span>}
                    </button>
                  ) : null}
                </div>
              </div>
              <div className="hidden md:grid grid-cols-[1fr_auto_1fr] max-w-[90rem] mx-auto px-6 lg:px-8 min-h-[4.25rem] py-2 items-center gap-4" data-testid="furnis-header-row">
                <BrandMark info={brandInfo} primary={primary} logoUrl={logoSrc} imgClass="store-logo-img" wrapClass="flex items-center justify-start gap-2" nameClass={`font-display tracking-[0.22em] text-xl uppercase ${ink}`} />
                <nav className={`flex items-center justify-center gap-5 lg:gap-7 text-[13px] min-w-0 overflow-visible ${ink}`} data-testid="furnis-menu-row">
                  {menu.map((m, i) => (
                    <NavItem key={i} base={base} item={m} className={`${ink} hover:opacity-80 whitespace-nowrap`} testid={`nav-${i}`} desktop />
                  ))}
                </nav>
                <div className="flex justify-end items-center gap-1" data-testid="furnis-header-icons-desktop">
                  {showSearch ? <SearchToggle shopHref={shopHref} navigate={navigate} look="furnis" className={`w-10 h-10 ${iconBtn}`} /> : null}
                  {showAccount ? (
                    <Link to={user ? `${base}/account` : `${base}/account/login`} className={`w-10 h-10 ${iconBtn}`} data-testid="store-account" aria-label={t("store.account")}>
                      <User className="w-5 h-5" />
                    </Link>
                  ) : null}
                  {showCart ? (
                    <button type="button" onClick={openCart} data-testid="store-cart-link" className={`relative w-10 h-10 ${iconBtn}`}>
                      <ShoppingBag className="w-5 h-5" />
                      {count > 0 && <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full text-[10px] font-bold" style={{ background: overlay ? "#ffffff" : primary, color: overlay ? "#1C1917" : "#ffffff" }}>{count}</span>}
                    </button>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
            );
          })()}
        </header>
      ) : bigshow ? (
        <header
          className={`${sticky ? "sticky top-0 z-50" : "relative z-50"} w-full ${bigshowHome ? "h-0 overflow-visible" : "bg-white"}`}
          data-testid="store-header-bigshow"
          data-bigshow-home={bigshowHome ? "true" : "false"}
          data-bigshow-scrolled={bigshowHome && bigshowScrolled ? "true" : "false"}
        >
          <style>{`
            .store-logo-img { height: ${logoHMobile}px; width: auto; max-width: min(48vw, 180px); object-fit: contain; }
            @media (min-width: 768px) {
              .store-logo-img { height: ${logoHDesktop}px; max-width: min(28vw, 220px); }
            }
          `}</style>
          <MobileMenuSheet {...menuSheetProps} hideTrigger look="bigshow" />
          {(() => {
            const overlay = bigshowHome && !bigshowScrolled;
            const ink = overlay ? "text-white" : "text-stone-900";
            const logoSrc = overlay ? info.logo_url : (info.logo_white_bg_url || info.logo_url);
            const iconBtn = `inline-flex items-center justify-center ${ink} ${overlay ? "hover:bg-white/10" : "hover:bg-stone-100"}`;
            return (
          <div className={`px-3 sm:px-5 ${bigshowHome ? "pt-3 pointer-events-none" : "py-2"}`}>
            <div className={`pointer-events-auto max-w-[90rem] mx-auto overflow-visible transition-colors duration-300 ${overlay ? "rounded-full bg-transparent border border-transparent" : "rounded-full bg-white shadow-[0_8px_30px_-12px_rgba(0,0,0,0.18)] border border-stone-100"}`}>
              <div className="md:hidden px-3 min-h-14 py-2 grid grid-cols-[1fr_auto_1fr] items-center gap-2" data-testid="bigshow-header-mobile">
                <button type="button" data-testid="bigshow-menu-btn" onClick={() => setMenuOpen(true)} className={`justify-self-start w-10 h-10 rounded-full ${iconBtn}`} aria-label="Menu">
                  <Menu className="w-5 h-5" />
                </button>
                <BrandMark info={brandInfo} primary={primary} logoUrl={logoSrc} imgClass="store-logo-img" wrapClass="flex items-center justify-center gap-2" nameClass={`font-extrabold tracking-tight truncate max-w-[120px] ${ink}`} />
                <div className="justify-self-end flex items-center gap-0.5" data-testid="bigshow-header-icons">
                  {showSearch ? <SearchToggle shopHref={shopHref} navigate={navigate} look="bigshow" testId="store-search-toggle-mobile" className={`w-9 h-9 rounded-full ${iconBtn}`} /> : null}
                  {showAccount ? (
                    <Link to={user ? `${base}/account` : `${base}/account/login`} className={`w-9 h-9 rounded-full ${iconBtn}`} data-testid="store-login-link" aria-label={t("store.account")}>
                      <User className="w-4 h-4" />
                    </Link>
                  ) : null}
                  {showCart ? (
                    <button type="button" onClick={openCart} data-testid="store-cart-link" className={`relative w-9 h-9 rounded-full ${iconBtn}`}>
                      <ShoppingBag className="w-4 h-4" />
                      {count > 0 && <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full text-[10px] font-bold text-white" style={{ background: overlay ? "#ffffff" : primary, color: overlay ? "#111111" : "#ffffff" }}>{count}</span>}
                    </button>
                  ) : null}
                </div>
              </div>
              <div className="hidden md:grid grid-cols-[1fr_auto_1fr] max-w-[90rem] mx-auto px-6 lg:px-8 min-h-[4.25rem] py-2 items-center gap-4" data-testid="bigshow-header-row">
                <nav className={`flex items-center justify-start gap-4 lg:gap-6 text-sm font-medium min-w-0 overflow-visible ${ink}`} data-testid="bigshow-menu-row">
                  {menu.map((m, i) => (
                    <NavItem key={i} base={base} item={m} className={`${ink} hover:opacity-80 whitespace-nowrap`} testid={`nav-${i}`} desktop />
                  ))}
                </nav>
                <BrandMark info={brandInfo} primary={primary} logoUrl={logoSrc} imgClass="store-logo-img" wrapClass="flex items-center justify-center gap-2 px-2" nameClass={`font-extrabold text-xl tracking-tight uppercase ${ink}`} />
                <div className="flex justify-end items-center gap-1" data-testid="bigshow-header-icons-desktop">
                  {showSearch ? <SearchToggle shopHref={shopHref} navigate={navigate} look="bigshow" className={`w-10 h-10 rounded-full ${iconBtn}`} /> : null}
                  {showAccount ? (
                    <Link to={user ? `${base}/account` : `${base}/account/login`} className={`w-10 h-10 rounded-full ${iconBtn}`} data-testid="store-account" aria-label={t("store.account")}>
                      <User className="w-5 h-5" />
                    </Link>
                  ) : null}
                  {showCart ? (
                    <button type="button" onClick={openCart} data-testid="store-cart-link" className={`relative w-10 h-10 rounded-full ${iconBtn}`}>
                      <ShoppingBag className="w-5 h-5" />
                      {count > 0 && <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full text-[10px] font-bold" style={{ background: overlay ? "#ffffff" : primary, color: overlay ? "#111111" : "#ffffff" }}>{count}</span>}
                    </button>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
            );
          })()}
        </header>
      ) : quicksto ? (
        <header className={`${sticky ? "sticky top-0 z-[80]" : "relative z-[80]"} w-full bg-white`} data-testid="store-header-quicksto">
          <style>{`
            [data-storefront-theme="quicksto"]:not(.sf-fontroot) { font-family: Inter, system-ui, sans-serif; }
            [data-storefront-theme="quicksto"]:not(.sf-fontroot) .font-display { font-family: Inter, system-ui, sans-serif; letter-spacing: -0.02em; }
            .store-logo-img { height: ${logoHMobile}px; width: auto; max-width: min(48vw, 180px); object-fit: contain; }
            @media (min-width: 768px) {
              .store-logo-img { height: ${logoHDesktop}px; max-width: min(28vw, 220px); }
            }
          `}</style>
          <QuickstoMenuSheet menuOpen={menuOpen} setMenuOpen={setMenuOpen} menu={menu} utility={utility} base={base} includeMain hideTrigger />
          <div style={{ background: primary }} data-testid="quicksto-logo-bar">
            <div className="md:hidden px-3 min-h-14 py-2 flex items-center gap-2" data-testid="quicksto-header-mobile">
              <button type="button" data-testid="quicksto-menu-btn" onClick={() => setMenuOpen(true)} className="inline-flex items-center justify-center w-10 h-10 rounded-lg text-white shrink-0 bg-white/15" aria-label="Menu">
                <Menu className="w-5 h-5" />
              </button>
              <div className="flex-1 min-w-0 flex justify-center">
                <BrandMark info={brandInfo} primary={secondary} imgClass="store-logo-img" wrapClass="flex items-center justify-center gap-2" nameClass="font-extrabold text-white truncate max-w-[120px]" />
              </div>
              <div className="flex items-center gap-0.5 shrink-0" data-testid="quicksto-header-icons">
                {showAccount ? (
                  <Link to={user ? `${base}/account` : `${base}/account/login`} className="inline-flex items-center justify-center w-9 h-9 text-white" data-testid="store-login-link" aria-label={t("store.account")}>
                    <User className="w-4 h-4" />
                  </Link>
                ) : null}
                <Link to={`${base}/wishlist`} data-testid="store-wishlist-link" className="relative inline-flex items-center justify-center w-9 h-9 text-white">
                  <Heart className="w-4 h-4" />
                  {wishlist?.count > 0 && <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full text-[10px] font-bold" style={{ background: secondary, color: primary }}>{wishlist.count}</span>}
                </Link>
                {showCart ? (
                  <button type="button" onClick={openCart} data-testid="store-cart-link" className="relative inline-flex items-center justify-center w-9 h-9 text-white">
                    <ShoppingBag className="w-4 h-4" />
                    <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full text-[10px] font-bold" style={{ background: secondary, color: primary }}>{count}</span>
                  </button>
                ) : null}
              </div>
            </div>
            {showSearch ? (
              <div className="md:hidden px-3 pb-3">
                <MixyCategorySearch shopHref={shopHref} navigate={navigate} categories={catNav} testId="quicksto-search-bar-mobile" placeholder="Search products..." allLabel="All" look="quicksto" />
              </div>
            ) : null}
            <div className="hidden md:flex max-w-[90rem] mx-auto px-6 lg:px-10 min-h-[4.25rem] py-3 items-center gap-4" data-testid="quicksto-header-row">
              <BrandMark info={brandInfo} primary={secondary} imgClass="store-logo-img" wrapClass="flex items-center gap-2 shrink-0" nameClass="font-extrabold text-xl text-white" />
              {showSearch ? (
                <div className="flex-1 max-w-xl min-w-0">
                  <MixyCategorySearch shopHref={shopHref} navigate={navigate} categories={catNav} testId="quicksto-search-bar" placeholder="Search products..." allLabel="All" look="quicksto" />
                </div>
              ) : <div className="flex-1" />}
              <div className="ml-auto flex items-center gap-1 shrink-0 text-white" data-testid="quicksto-header-icons-desktop">
                {showWhats ? (
                  <a href={`https://wa.me/${info.whatsapp.replace(/[^0-9]/g, "")}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center w-10 h-10" data-testid="store-whatsapp" aria-label="WhatsApp">
                    <MessageSquareText className="w-5 h-5" />
                  </a>
                ) : null}
                {showPhone ? (
                  <a href={`tel:${info.phone}`} className="inline-flex items-center justify-center w-10 h-10" data-testid="store-phone-link" aria-label="Phone">
                    <Phone className="w-5 h-5" />
                  </a>
                ) : null}
                {showCart ? (
                  <button type="button" onClick={openCart} data-testid="store-cart-link" className="relative inline-flex items-center justify-center w-10 h-10">
                    <ShoppingBag className="w-5 h-5" />
                    <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full text-[10px] font-bold" style={{ background: secondary, color: primary }}>{count}</span>
                  </button>
                ) : null}
                <Link to={`${base}/wishlist`} data-testid="store-wishlist-link" className="relative inline-flex items-center justify-center w-10 h-10">
                  <Heart className="w-5 h-5" />
                  {wishlist?.count > 0 && <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full text-[10px] font-bold" style={{ background: secondary, color: primary }}>{wishlist.count}</span>}
                </Link>
                {showAccount ? (
                  <Link to={user ? `${base}/account` : `${base}/account/login`} className="inline-flex items-center justify-center w-10 h-10" data-testid="store-account" aria-label={t("store.account")}>
                    <User className="w-5 h-5" />
                  </Link>
                ) : null}
              </div>
            </div>
          </div>
          <div className="hidden md:block border-t border-white/15" style={{ background: primary }}>
            <nav className="max-w-[90rem] mx-auto px-6 lg:px-10 flex items-center justify-center gap-x-5 lg:gap-x-6 min-h-11 py-1.5 flex-wrap overflow-visible text-sm font-semibold text-white relative z-[90]" data-testid="quicksto-menu-row">
              {menu.map((m, i) => (
                <NavItem key={i} base={base} item={m} className="text-white hover:text-white/80 whitespace-nowrap" testid={`nav-${i}`} desktop />
              ))}
            </nav>
          </div>
        </header>
      ) : locally ? (
        <header className={`${sticky ? "sticky top-0 z-[80]" : "relative z-[80]"} w-full bg-white`} data-testid="store-header-locally">
          <style>{`
            [data-storefront-theme="locally"]:not(.sf-fontroot) { font-family: Inter, system-ui, sans-serif; }
            [data-storefront-theme="locally"]:not(.sf-fontroot) .font-display { font-family: Inter, system-ui, sans-serif; letter-spacing: -0.02em; }
            .store-logo-img { height: ${logoHMobile}px; width: auto; max-width: min(48vw, 180px); object-fit: contain; }
            @media (min-width: 768px) {
              .store-logo-img { height: ${logoHDesktop}px; max-width: min(28vw, 220px); }
            }
          `}</style>
          <LocallyMenuSheet menuOpen={menuOpen} setMenuOpen={setMenuOpen} menu={menu} utility={utility} base={base} includeMain hideTrigger />
          <div className="text-white" style={{ background: primary }} data-testid="locally-utility-bar">
            <div className="max-w-[90rem] mx-auto px-4 sm:px-6 lg:px-10 min-h-12 py-2.5 hidden md:flex items-center justify-between gap-4 text-[13px]">
              <div className="flex items-center gap-4 min-w-0 overflow-x-auto no-scrollbar">
                {utility.map((m, i) => {
                  const last = i === utility.length - 1;
                  return (
                    <SmartLink
                      key={i}
                      base={base}
                      item={m}
                      testid={`util-nav-${i}`}
                      className={last ? "shrink-0 inline-flex items-center font-bold" : "shrink-0 opacity-90 hover:opacity-100"}
                      style={last ? { color: secondary } : undefined}
                    />
                  );
                })}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {showWhats ? (
                  <a href={`https://wa.me/${info.whatsapp.replace(/[^0-9]/g, "")}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center w-7 h-7 rounded-full" style={{ background: secondary, color: primary }} data-testid="store-whatsapp" aria-label="WhatsApp">
                    <MessageSquareText className="w-3.5 h-3.5" />
                  </a>
                ) : null}
                {showPhone ? (
                  <a href={`tel:${info.phone}`} className="inline-flex items-center justify-center w-7 h-7 rounded-full" style={{ background: secondary, color: primary }} data-testid="store-phone-link" aria-label="Phone">
                    <Phone className="w-3.5 h-3.5" />
                  </a>
                ) : null}
              </div>
            </div>
          </div>
          <div className="h-px" style={{ background: primary }} data-testid="locally-utility-divider">
            <div className="max-w-[90rem] mx-auto h-px bg-white/25" />
          </div>
          <div style={{ background: primary }} data-testid="locally-logo-bar">
          <div className="md:hidden px-3 min-h-14 py-2 flex items-center gap-2" data-testid="locally-header-mobile">
            <button type="button" data-testid="locally-menu-btn" onClick={() => setMenuOpen(true)} className="inline-flex items-center justify-center w-10 h-10 rounded-lg text-white shrink-0 bg-white/15" aria-label="Menu">
              <Menu className="w-5 h-5" />
            </button>
            <div className="flex-1 min-w-0 flex justify-center">
              <BrandMark info={brandInfo} primary={secondary} imgClass="store-logo-img" wrapClass="flex items-center justify-center gap-2" nameClass="font-extrabold text-white truncate max-w-[120px]" />
            </div>
            <div className="flex items-center gap-1 shrink-0" data-testid="locally-header-icons">
              {showAccount ? (
                <Link to={user ? `${base}/account` : `${base}/account/login`} className="inline-flex items-center justify-center w-9 h-9 rounded-full" style={{ background: secondary, color: primary }} data-testid="store-login-link" aria-label={t("store.account")}>
                  <User className="w-4 h-4" />
                </Link>
              ) : null}
              <Link to={`${base}/wishlist`} data-testid="store-wishlist-link" className="relative inline-flex items-center justify-center w-9 h-9 rounded-full" style={{ background: secondary, color: primary }}>
                <Heart className="w-4 h-4" />
                {wishlist?.count > 0 && <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full text-white text-[10px] font-bold" style={{ background: primary }}>{wishlist.count}</span>}
              </Link>
              {showCart ? (
                <button type="button" onClick={openCart} data-testid="store-cart-link" className="relative inline-flex items-center justify-center w-9 h-9 rounded-full" style={{ background: secondary, color: primary }}>
                  <ShoppingBag className="w-4 h-4" />
                  <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full text-white text-[10px] font-bold" style={{ background: primary }}>{count}</span>
                </button>
              ) : null}
            </div>
          </div>
          {showSearch ? (
            <div className="md:hidden px-3 pb-3">
              <MixyCategorySearch shopHref={shopHref} navigate={navigate} categories={catNav} testId="locally-search-bar-mobile" placeholder="Search popular products..." allLabel="All" look="locally" />
            </div>
          ) : null}
          <div className="hidden md:flex max-w-[90rem] mx-auto px-6 lg:px-10 min-h-[4.5rem] py-3 items-center gap-4" data-testid="locally-header-row">
            <BrandMark info={brandInfo} primary={secondary} imgClass="store-logo-img" wrapClass="flex items-center gap-2 shrink-0" nameClass="font-extrabold text-xl text-white" />
            {showSearch ? (
              <MixyCategorySearch shopHref={shopHref} navigate={navigate} categories={catNav} testId="locally-search-bar" placeholder="Search popular products..." allLabel="All" look="locally" />
            ) : <div className="flex-1" />}
            <div className="flex items-center gap-2 shrink-0" data-testid="locally-header-icons-desktop">
              {showAccount ? (
                <Link to={user ? `${base}/account` : `${base}/account/login`} className="inline-flex items-center justify-center w-10 h-10 rounded-full" style={{ background: secondary, color: primary }} data-testid="store-account" aria-label={t("store.account")}>
                  <User className="w-5 h-5" />
                </Link>
              ) : null}
              <Link to={`${base}/wishlist`} data-testid="store-wishlist-link" className="relative inline-flex items-center justify-center w-10 h-10 rounded-full" style={{ background: secondary, color: primary }}>
                <Heart className="w-5 h-5" />
                {wishlist?.count > 0 && <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full text-white text-[10px] font-bold" style={{ background: primary }}>{wishlist.count}</span>}
              </Link>
              {showCart ? (
                <button type="button" onClick={openCart} data-testid="store-cart-link" className="relative inline-flex items-center justify-center w-10 h-10 rounded-full" style={{ background: secondary, color: primary }}>
                  <ShoppingBag className="w-5 h-5" />
                  <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full text-white text-[10px] font-bold" style={{ background: primary }}>{count}</span>
                </button>
              ) : null}
            </div>
          </div>
          </div>
          <div className="h-px bg-stone-100" data-testid="locally-header-divider" />
          <div className="hidden md:flex max-w-[90rem] mx-auto px-6 lg:px-10 h-11 items-center gap-4" data-testid="locally-menu-row">
            <nav className="flex items-center gap-4 lg:gap-6 text-sm font-semibold text-stone-800 min-w-0 flex-wrap overflow-visible relative z-[90]">
              {menu.map((m, i) => (
                <NavItem key={i} base={base} item={m} className="hover:opacity-70 whitespace-nowrap" testid={`nav-${i}`} desktop />
              ))}
            </nav>
            <Link to={`${base}/shop?featured=true`} className="ml-auto shrink-0 inline-flex items-center gap-1.5 text-sm font-bold" style={{ color: primary }} data-testid="locally-sale-tag">
              <Percent className="w-4 h-4" /> Best Seller <span className="px-1.5 py-0.5 rounded text-[10px] font-extrabold uppercase tracking-wide text-white" style={{ background: "#EF4444" }}>Sale</span>
            </Link>
          </div>
        </header>
      ) : beast ? (
        <header className={`${sticky ? "sticky top-0 z-[80]" : "relative z-[80]"} w-full bg-black border-b border-white/10`} data-testid="store-header-beast">
          <style>{`
            [data-storefront-theme="beast"]:not(.sf-fontroot) { font-family: Inter, system-ui, sans-serif; }
            [data-storefront-theme="beast"]:not(.sf-fontroot) .font-display { font-family: Inter, system-ui, sans-serif; letter-spacing: -0.02em; }
            .store-logo-img { height: ${logoHMobile}px; width: auto; max-width: min(48vw, 180px); object-fit: contain; }
            @media (min-width: 768px) {
              .store-logo-img { height: ${logoHDesktop}px; max-width: min(28vw, 220px); }
            }
          `}</style>
          <BeastMenuSheet menuOpen={menuOpen} setMenuOpen={setMenuOpen} menu={menu} utility={utility} base={base} includeMain hideTrigger />
          <div className="grid grid-cols-3 items-center min-h-14 md:min-h-16 px-4 sm:px-6 lg:px-10" data-testid="beast-header-row">
            <div className="justify-self-start" data-testid="beast-header-mobile">
              <button type="button" data-testid="beast-menu-btn" onClick={() => setMenuOpen(true)} className="inline-flex items-center justify-center w-10 h-10 text-white" aria-label="Menu">
                <Menu className="w-5 h-5" />
              </button>
            </div>
            <div className="justify-self-center min-w-0">
              <BrandMark info={brandInfo} primary={primary} imgClass="store-logo-img" wrapClass="flex items-center justify-center gap-2" nameClass="font-extrabold text-white lowercase tracking-tight text-lg sm:text-xl truncate max-w-[40vw]" />
            </div>
            <div className="justify-self-end flex items-center gap-0.5" data-testid="beast-header-icons">
              {showSearch ? <SearchToggle shopHref={shopHref} navigate={navigate} look="beast" /> : null}
              <Link to={`${base}/wishlist`} data-testid="store-wishlist-link" className="relative inline-flex items-center justify-center w-9 h-9 text-white">
                <Heart className="w-5 h-5" />
                {wishlist?.count > 0 && <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-white text-black text-[10px] font-bold">{wishlist.count}</span>}
              </Link>
              {showAccount ? (
                <Link to={user ? `${base}/account` : `${base}/account/login`} data-testid={user ? "store-account" : "store-login-link"} className="hidden sm:inline-flex items-center justify-center w-9 h-9 text-white" aria-label={t("store.account")}>
                  <User className="w-5 h-5" />
                </Link>
              ) : null}
              {showCart ? (
                <button type="button" onClick={openCart} data-testid="store-cart-link" className="relative inline-flex items-center justify-center w-9 h-9 text-white">
                  <ShoppingBag className="w-5 h-5" />
                  <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-white text-black text-[10px] font-bold">{count}</span>
                </button>
              ) : null}
            </div>
          </div>
        </header>
      ) : various ? (
        <header className={`${sticky ? "sticky top-0 z-[80]" : "relative z-[80]"} w-full bg-white border-b border-stone-200`} data-testid="store-header-various">
          <style>{`
            [data-storefront-theme="various"]:not(.sf-fontroot) { font-family: Inter, system-ui, sans-serif; }
            [data-storefront-theme="various"]:not(.sf-fontroot) .font-display { font-family: Inter, system-ui, sans-serif; letter-spacing: -0.02em; }
            .store-logo-img { height: ${logoHMobile}px; width: auto; max-width: min(48vw, 180px); object-fit: contain; }
            @media (min-width: 768px) {
              .store-logo-img { height: ${logoHDesktop}px; max-width: min(28vw, 220px); }
            }
          `}</style>
          <VariousMenuSheet menuOpen={menuOpen} setMenuOpen={setMenuOpen} menu={menu} utility={utility} base={base} includeMain hideTrigger />
          <div className="md:hidden px-3 min-h-14 py-2 flex items-center gap-2" data-testid="various-header-mobile">
            <button type="button" data-testid="various-menu-btn" onClick={() => setMenuOpen(true)} className="inline-flex items-center justify-center w-10 h-10 rounded-lg text-white shrink-0" style={{ background: "var(--store-primary, #1B4332)" }} aria-label="Menu">
              <Menu className="w-5 h-5" />
            </button>
            <div className="flex-1 min-w-0 flex justify-center">
              <BrandMark info={brandInfo} primary={primary} imgClass="store-logo-img" wrapClass="flex items-center justify-center gap-2" nameClass="font-extrabold text-stone-900 truncate max-w-[120px]" />
            </div>
            <div className="flex items-center gap-0.5 shrink-0" data-testid="various-header-icons">
              <Link to={`${base}/wishlist`} data-testid="store-wishlist-link" className="relative inline-flex items-center justify-center w-9 h-9 text-stone-800">
                <Heart className="w-5 h-5" />
                {wishlist?.count > 0 && <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full text-white text-[10px] font-bold" style={{ background: "var(--store-primary, #1B4332)" }}>{wishlist.count}</span>}
              </Link>
              {showCart ? (
                <button type="button" onClick={openCart} data-testid="store-cart-link" className="relative inline-flex items-center justify-center w-9 h-9 text-stone-800">
                  <ShoppingBag className="w-5 h-5" />
                  <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full text-white text-[10px] font-bold" style={{ background: "var(--store-primary, #1B4332)" }}>{count}</span>
                </button>
              ) : null}
            </div>
          </div>
          {showSearch ? (
            <div className="md:hidden px-3 pb-2">
              <MixyCategorySearch shopHref={shopHref} navigate={navigate} categories={catNav} testId="various-search-bar-mobile" placeholder="What are you searching for?" allLabel="All categories" showMic look="various" />
            </div>
          ) : null}
          <div className="hidden md:flex max-w-[90rem] mx-auto px-6 lg:px-10 min-h-[4.25rem] py-2 items-center gap-4" data-testid="various-header-row">
            <button type="button" data-testid="various-menu-btn-desktop" onClick={() => setMenuOpen(true)} className="inline-flex items-center gap-2 h-10 px-3.5 rounded-lg text-white text-sm font-bold shrink-0" style={{ background: "var(--store-primary, #1B4332)" }}>
              <Menu className="w-4 h-4" /> Menu
            </button>
            <BrandMark info={brandInfo} primary={primary} imgClass="store-logo-img" wrapClass="flex items-center gap-2 shrink-0" nameClass="font-extrabold text-xl text-stone-900" />
            {showSearch ? (
              <MixyCategorySearch shopHref={shopHref} navigate={navigate} categories={catNav} testId="various-search-bar" placeholder="What are you searching for?" allLabel="All categories" showMic look="various" />
            ) : <div className="flex-1" />}
            <div className="flex items-center gap-3 shrink-0" data-testid="various-header-icons-desktop">
              {showAccount ? (
                <Link to={user ? `${base}/account` : `${base}/account/login`} className="inline-flex items-center justify-center w-9 h-9 text-stone-800" data-testid="store-login-link" aria-label={t("store.signIn")}>
                  <User className="w-5 h-5" />
                </Link>
              ) : null}
              <Link to={`${base}/wishlist`} data-testid="store-wishlist-link" className="relative inline-flex items-center justify-center w-9 h-9 text-stone-800">
                <Heart className="w-5 h-5" />
                {wishlist?.count > 0 && <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full text-white text-[10px] font-bold" style={{ background: "var(--store-primary, #1B4332)" }}>{wishlist.count}</span>}
              </Link>
              {showCart ? (
                <button type="button" onClick={openCart} data-testid="store-cart-link" className="relative inline-flex items-center justify-center w-9 h-9 text-stone-800">
                  <ShoppingBag className="w-5 h-5" />
                  <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full text-white text-[10px] font-bold" style={{ background: "var(--store-primary, #1B4332)" }}>{count}</span>
                </button>
              ) : null}
            </div>
          </div>
          <nav className="hidden md:flex max-w-[90rem] mx-auto px-6 lg:px-10 items-center justify-center gap-x-5 lg:gap-x-6 min-h-11 py-1.5 flex-wrap overflow-visible border-t border-stone-100 text-sm font-semibold text-stone-800 relative z-[90]" data-testid="various-menu-row">
            {menu.map((m, i) => (
              <NavItem key={i} base={base} item={m} className="hover:text-stone-950 whitespace-nowrap" testid={`nav-${i}`} desktop />
            ))}
          </nav>
        </header>
      ) : mixy ? (
        <header className={`${sticky ? "sticky top-0 z-[80]" : "relative z-[80]"} w-full bg-white border-b border-stone-200`} data-testid="store-header-mixy">
          <style>{`
            [data-storefront-theme="mixy"]:not(.sf-fontroot) { font-family: Inter, system-ui, sans-serif; }
            [data-storefront-theme="mixy"]:not(.sf-fontroot) .font-display { font-family: Inter, system-ui, sans-serif; letter-spacing: -0.02em; }
            .store-logo-img { height: ${logoHMobile}px; width: auto; max-width: min(48vw, 180px); object-fit: contain; }
            @media (min-width: 768px) {
              .store-logo-img { height: ${logoHDesktop}px; max-width: min(28vw, 220px); }
            }
          `}</style>
          <MixyMenuSheet menuOpen={menuOpen} setMenuOpen={setMenuOpen} menu={menu} utility={utility} base={base} includeMain hideTrigger />
          <div className="md:hidden px-3 min-h-14 py-2 flex items-center gap-2" data-testid="mixy-header-mobile">
            <button type="button" data-testid="mixy-menu-btn" onClick={() => setMenuOpen(true)} className="inline-flex items-center gap-2 h-10 px-3 rounded-lg bg-stone-950 text-white text-sm font-bold shrink-0">
              <Menu className="w-4 h-4" /> Menu
            </button>
            <div className="flex-1 min-w-0 flex justify-center">
              <BrandMark info={brandInfo} primary={primary} imgClass="store-logo-img" wrapClass="flex items-center justify-center gap-2" nameClass="font-extrabold text-stone-900 truncate max-w-[120px]" />
            </div>
            <div className="flex items-center gap-0.5 shrink-0">
              {showAccount ? (
                <Link to={user ? `${base}/account` : `${base}/account/login`} className="inline-flex flex-col items-center justify-center w-9 text-stone-800" data-testid="store-login-link" aria-label={t("store.signIn")}>
                  <User className="w-5 h-5" />
                </Link>
              ) : null}
              <Link to={`${base}/wishlist`} data-testid="store-wishlist-link" className="relative inline-flex items-center justify-center w-9 h-9 text-stone-800">
                <Heart className="w-5 h-5" />
                {wishlist?.count > 0 && <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-stone-900 text-white text-[10px] font-bold">{wishlist.count}</span>}
              </Link>
              {showCart ? (
                <button type="button" onClick={openCart} data-testid="store-cart-link" className="relative inline-flex items-center justify-center w-9 h-9 text-stone-800">
                  <ShoppingBag className="w-5 h-5" />
                  <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-stone-900 text-white text-[10px] font-bold">{count}</span>
                </button>
              ) : null}
            </div>
          </div>
          {showSearch ? (
            <div className="md:hidden px-3 pb-2">
              <MixyCategorySearch shopHref={shopHref} navigate={navigate} categories={catNav} testId="mixy-search-bar-mobile" look="mixy" />
            </div>
          ) : null}
          <div className="hidden md:flex max-w-[90rem] mx-auto px-6 lg:px-10 min-h-[4.25rem] py-2 items-center gap-4" data-testid="mixy-header-row">
            <BrandMark info={brandInfo} primary={primary} imgClass="store-logo-img" wrapClass="flex items-center gap-2 shrink-0" nameClass="font-extrabold text-xl text-stone-900" />
            <button type="button" data-testid="mixy-menu-btn-desktop" onClick={() => setMenuOpen(true)} className="inline-flex items-center gap-2 h-10 px-3.5 rounded-lg bg-stone-950 text-white text-sm font-bold shrink-0">
              <Menu className="w-4 h-4" /> Menu
            </button>
            {showSearch ? <MixyCategorySearch shopHref={shopHref} navigate={navigate} categories={catNav} look="mixy" /> : <div className="flex-1" />}
            <div className="ml-0 flex items-center gap-5 shrink-0" data-testid="mixy-header-icons">
              <Link to={`${base}/wishlist`} data-testid="store-wishlist-link" className="relative inline-flex flex-col items-center text-stone-800 hover:opacity-70">
                <Heart className="w-5 h-5" />
                <span className="text-[11px] font-medium mt-0.5">Wishlist</span>
                {wishlist?.count > 0 && <span className="absolute -top-1 -right-2 min-w-[16px] h-4 px-1 rounded-full bg-stone-900 text-white text-[10px] font-bold">{wishlist.count}</span>}
              </Link>
              {showCart ? (
                <button type="button" onClick={openCart} data-testid="store-cart-link" className="relative inline-flex flex-col items-center text-stone-800 hover:opacity-70">
                  <ShoppingBag className="w-5 h-5" />
                  <span className="text-[11px] font-medium mt-0.5">Cart</span>
                  <span className="absolute -top-1 right-0 min-w-[16px] h-4 px-1 rounded-full bg-stone-900 text-white text-[10px] font-bold">{count}</span>
                </button>
              ) : null}
            </div>
          </div>
          <div className="hidden md:block border-t border-stone-100 overflow-visible" data-testid="mixy-menu-row">
            <div className="max-w-[90rem] mx-auto px-6 lg:px-10 min-h-12 flex items-center gap-4 overflow-visible">
              <Link to={`${base}/shop`} className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-stone-900 text-sm font-bold shrink-0" style={{ background: "var(--mixy-accent, #FFDC2E)" }} data-testid="mixy-sale-tag">
                <Flame className="w-3.5 h-3.5" /> Sale
              </Link>
              <nav className="flex items-center gap-4 lg:gap-5 text-sm font-semibold text-stone-800 min-w-0 flex-wrap overflow-visible">
                {menu.map((m, i) => (
                  <NavItem key={i} base={base} item={m} className="hover:text-stone-950 whitespace-nowrap" testid={`nav-${i}`} desktop />
                ))}
              </nav>
              <div className="ml-auto flex items-center gap-3 shrink-0 text-sm font-medium text-stone-700" data-testid="mixy-menu-tools">
                {showAccount ? (
                  <Link to={user ? `${base}/account` : `${base}/account/login`} className="hover:text-stone-950 whitespace-nowrap" data-testid="store-account">{t("store.myAccount")}</Link>
                ) : null}
                {showWhats ? (
                  <a href={`https://wa.me/${info.whatsapp.replace(/[^0-9]/g, "")}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 hover:text-stone-950" data-testid="store-whatsapp">
                    <MessageSquareText className="w-4 h-4" /> WhatsApp
                  </a>
                ) : null}
                {showPhone ? (
                  <a href={`tel:${info.phone}`} className="inline-flex items-center gap-1 hover:text-stone-950" data-testid="store-phone-link">
                    <Phone className="w-4 h-4" /> {info.phone}
                  </a>
                ) : null}
              </div>
            </div>
          </div>
        </header>
      ) : perfecto ? (
        <header className={`${sticky ? "sticky top-0 z-[80]" : "relative z-[80]"} w-full bg-white border-b border-stone-100`} data-testid="store-header-perfecto">
          <style>{`
            [data-storefront-theme="perfecto"]:not(.sf-fontroot) .font-display { font-family: "Playfair Display", Georgia, serif; letter-spacing: -0.02em; }
            .store-logo-img { height: ${logoHMobile}px; width: auto; max-width: min(56vw, 200px); object-fit: contain; }
            @media (min-width: 768px) {
              .store-logo-img { height: ${logoHDesktop}px; max-width: min(32vw, 240px); }
            }
          `}</style>
          <div className="md:hidden px-3 min-h-14 py-2 grid grid-cols-[auto_1fr_auto] items-center gap-2">
            <MobileMenuSheet {...menuSheetProps} />
            <div className="flex justify-center min-w-0">
              <BrandMark info={brandInfo} primary={primary} imgClass="store-logo-img" wrapClass="flex items-center justify-center gap-2" nameClass="font-display font-bold text-[#3E2723] truncate max-w-[140px]" />
            </div>
            <HeaderIcons {...iconProps} look="perfecto" compact />
          </div>
          <div className="hidden md:grid grid-cols-[auto_minmax(0,1fr)_auto] max-w-[90rem] mx-auto px-6 lg:px-10 min-h-[4.25rem] py-2 items-center gap-6" data-testid="perfecto-header-row">
            <BrandMark info={brandInfo} primary={primary} imgClass="store-logo-img" wrapClass="flex items-center gap-2 shrink-0" nameClass="font-display font-bold text-xl text-[#3E2723]" />
            <div />
            <div className="justify-self-end shrink-0" data-testid="perfecto-header-icons">
              <HeaderIcons {...iconProps} look="perfecto" />
            </div>
          </div>
          <div className="hidden md:block border-t border-stone-100" data-testid="store-cat-nav">
            <nav className="max-w-[90rem] mx-auto px-6 lg:px-10 flex items-center justify-center gap-x-5 lg:gap-x-6 min-h-11 py-1.5 flex-wrap overflow-visible relative z-[90]" data-testid="perfecto-menu-row">
              {(menu.length ? menu : catNav.map((c) => ({ label: c.name, path: `shop?category=${c.slug}` }))).map((m, i) => (
                <NavItem key={i} base={base} item={m} className="text-[11px] uppercase tracking-[0.14em] font-semibold text-stone-700 hover:text-stone-950 whitespace-nowrap" testid={`nav-${i}`} desktop />
              ))}
            </nav>
          </div>
        </header>
      ) : boxy ? (
        <header className={`${sticky ? "sticky top-0 z-[80]" : "relative z-[80]"} w-full border-b border-stone-200/80`} style={{ background: "#F7F4EE" }} data-testid="store-header-boxy">
          <style>{`
            [data-storefront-theme="boxy"]:not(.sf-fontroot) .font-display { font-family: "Playfair Display", Georgia, serif; letter-spacing: -0.02em; }
            .store-logo-img { height: ${logoHMobile}px; width: auto; max-width: min(56vw, 200px); object-fit: contain; }
            @media (min-width: 768px) {
              .store-logo-img { height: ${logoHDesktop}px; max-width: min(36vw, 280px); }
            }
          `}</style>
          <MobileMenuSheet {...menuSheetProps} hideTrigger />
          <div className="md:hidden px-3 min-h-14 py-2 grid grid-cols-[1fr_auto_1fr] items-center gap-2" data-testid="boxy-header-mobile">
            <div className="justify-self-start">
              {showSearch ? <SearchToggle shopHref={shopHref} navigate={navigate} look="boxy" testId="store-search-toggle-mobile" /> : <span />}
            </div>
            <div className="flex justify-center min-w-0">
              <BrandMark info={brandInfo} primary={primary} imgClass="store-logo-img" wrapClass="flex items-center justify-center gap-2" nameClass="font-display font-semibold text-[color:var(--store-primary)] truncate max-w-[160px] uppercase tracking-[0.12em] text-sm" />
            </div>
            <div className="justify-self-end" data-testid="boxy-header-wishlist">
              <Link to={`${base}/wishlist`} data-testid="store-wishlist-link-mobile" className="relative inline-flex items-center justify-center w-9 h-9 text-[color:var(--store-primary)] hover:opacity-70" aria-label="Wishlist">
                <Heart className="w-5 h-5" strokeWidth={1.5} />
                {wishlist?.count > 0 && <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-[var(--store-primary)] text-white text-[10px] font-bold flex items-center justify-center font-mono-nums">{wishlist.count}</span>}
              </Link>
            </div>
          </div>
          <div className="hidden md:grid grid-cols-[1fr_auto_1fr] max-w-[90rem] mx-auto px-6 lg:px-10 min-h-[4.5rem] py-2 items-center gap-6" data-testid="boxy-header-row">
            <div className="justify-self-start" data-testid="boxy-header-search">
              {showSearch ? <SearchToggle shopHref={shopHref} navigate={navigate} look="boxy" /> : null}
            </div>
            <BrandMark info={brandInfo} primary={primary} imgClass="store-logo-img" wrapClass="flex items-center justify-center gap-2 px-2" nameClass="font-display font-semibold text-xl tracking-[0.18em] uppercase text-[color:var(--store-primary)]" />
            <div className="justify-self-end shrink-0" data-testid="boxy-header-icons">
              <HeaderIcons {...iconProps} showSearch={false} look="boxy" />
            </div>
          </div>
          <div className="hidden md:block border-t border-stone-200/70" data-testid="store-cat-nav">
            <nav className="max-w-[90rem] mx-auto px-6 lg:px-10 flex items-center justify-center gap-x-5 lg:gap-x-6 min-h-11 py-1.5 flex-wrap overflow-visible relative z-[90]" data-testid="boxy-menu-row">
              {(menu.length ? menu : catNav.map((c) => ({ label: c.name, path: `shop?category=${c.slug}` }))).map((m, i) => (
                <NavItem key={i} base={base} item={m} className="text-[11px] uppercase tracking-[0.16em] font-semibold text-[color:var(--store-primary)] hover:opacity-70 whitespace-nowrap" testid={`nav-${i}`} desktop />
              ))}
            </nav>
          </div>
        </header>
      ) : medico ? (
        <header className={`${sticky ? "sticky top-0 z-[80]" : "relative z-[80]"} w-full bg-white border-b border-stone-100`} data-testid="store-header-medico">
          <style>{`
            .store-logo-img { height: ${logoHMobile}px; width: auto; max-width: min(56vw, 200px); object-fit: contain; }
            @media (min-width: 768px) {
              .store-logo-img { height: ${logoHDesktop}px; max-width: min(32vw, 240px); }
            }
          `}</style>
          <div className="md:hidden px-3 min-h-16 py-2 grid grid-cols-[auto_1fr_auto] items-center gap-2">
            <MobileMenuSheet {...menuSheetProps} />
            <div className="flex justify-center min-w-0">
              <BrandMark info={brandInfo} primary={primary} imgClass="store-logo-img" wrapClass="flex items-center justify-center gap-2" nameClass="font-display font-bold text-[color:var(--store-primary)] truncate max-w-[140px]" />
            </div>
            <div className="flex items-center justify-end gap-0.5">
              <HeaderIcons {...iconProps} look="medico" compact />
            </div>
          </div>
          <div className="hidden md:grid grid-cols-[auto_minmax(0,1fr)_auto] max-w-7xl mx-auto px-4 lg:px-8 min-h-[4.5rem] py-2 items-center gap-6" data-testid="medico-header-row">
            <BrandMark info={brandInfo} primary={primary} imgClass="store-logo-img" wrapClass="flex items-center gap-2 shrink-0" nameClass="font-display font-extrabold text-xl tracking-tight text-[color:var(--store-primary)]" />
            <nav className="flex items-center justify-center gap-5 lg:gap-7 text-sm font-medium text-stone-800 min-w-0 flex-wrap" data-testid="medico-menu-row">
              {menu.map((m, i) => (
                <NavItem key={i} base={base} item={m} className="hover:text-[color:var(--store-primary)] whitespace-nowrap" testid={`nav-${i}`} desktop />
              ))}
            </nav>
            <div className="justify-self-end shrink-0" data-testid="medico-header-icons">
              <HeaderIcons {...iconProps} look="medico" />
            </div>
          </div>
        </header>
      ) : modern ? (
        <header className={`${sticky ? "sticky top-0 z-[80]" : "relative z-[80]"} w-full bg-white border-b border-neutral-200`} data-testid="store-header-modern">
          <style>{`
            .store-logo-img { height: ${logoHMobile}px; width: auto; max-width: min(56vw, 200px); object-fit: contain; }
            @media (min-width: 768px) {
              .store-logo-img { height: ${logoHDesktop}px; max-width: min(28vw, 220px); }
            }
          `}</style>
          <div className="md:hidden px-3 min-h-16 py-2 grid grid-cols-[auto_1fr_auto] items-center gap-2">
            <MobileMenuSheet {...menuSheetProps} />
            <div className="flex justify-center min-w-0">
              <BrandMark info={brandInfo} primary={primary} imgClass="store-logo-img" wrapClass="flex items-center justify-center gap-2" nameClass="font-display font-bold text-stone-900 truncate max-w-[140px]" />
            </div>
            <div className="flex items-center justify-end gap-1">
              {showSearch ? <SearchToggle shopHref={shopHref} navigate={navigate} testId="store-search-toggle-mobile" /> : null}
              {showCart ? (
              <Button size="sm" onClick={openCart} data-testid="store-cart-link-mobile" className="btn-store-primary rounded-full relative h-9 w-9 p-0">
                <ShoppingCart className="w-4 h-4" />
                {count > 0 && <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full bg-stone-900 text-white text-[10px] font-bold">{count}</span>}
              </Button>
              ) : <span />}
            </div>
          </div>
          <div className="hidden md:grid max-w-7xl mx-auto px-4 lg:px-8 min-h-[4.5rem] py-2 grid-cols-[1fr_auto_1fr] items-center gap-4">
            <nav className="flex items-center justify-start gap-5 lg:gap-7 text-[13px] uppercase tracking-[0.14em] font-semibold text-stone-700 min-w-0 flex-wrap">
              {menu.map((m, i) => (
                <NavItem key={i} base={base} item={m} className="hover:text-stone-950" testid={`nav-${i}`} desktop />
              ))}
            </nav>
            <BrandMark info={brandInfo} primary={primary} imgClass="store-logo-img" wrapClass="flex items-center justify-center gap-2 px-2" nameClass="font-display font-extrabold text-xl tracking-tight text-stone-900" />
            <div className="flex justify-end min-w-0">
              <HeaderIcons {...iconProps} />
            </div>
          </div>
        </header>
      ) : trendy ? (
        <header className={`${sticky ? "sticky top-0 z-[80]" : "relative z-[80]"} w-full bg-white border-b border-stone-100`} data-testid="store-header-trendy">
          <style>{`
            .store-logo-img { height: ${logoHMobile}px; width: auto; max-width: min(56vw, 200px); object-fit: contain; }
            @media (min-width: 768px) {
              .store-logo-img { height: ${logoHDesktop}px; max-width: min(32vw, 240px); }
            }
          `}</style>
          <div className="md:hidden px-3 min-h-16 py-2 grid grid-cols-[auto_1fr_auto] items-center gap-2">
            <MobileMenuSheet {...menuSheetProps} />
            <div className="flex justify-center min-w-0">
              <BrandMark info={brandInfo} primary={primary} imgClass="store-logo-img" wrapClass="flex items-center justify-center gap-2" nameClass="font-display font-bold text-stone-900 truncate max-w-[140px]" />
            </div>
            <div className="flex items-center justify-end gap-1">
              {showSearch ? <SearchToggle shopHref={shopHref} navigate={navigate} testId="store-search-toggle-mobile" /> : null}
              {showCart ? (
              <Button size="sm" onClick={openCart} data-testid="store-cart-link-mobile" className="btn-store-primary rounded-full relative h-9 w-9 p-0">
                <ShoppingCart className="w-4 h-4" />
                <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full bg-rose-600 text-white text-[10px] font-bold">{count}</span>
              </Button>
              ) : <span />}
            </div>
          </div>
          <div className="hidden md:flex max-w-7xl mx-auto px-4 lg:px-8 min-h-[4.5rem] py-2 items-center gap-5">
            <BrandMark info={brandInfo} primary={primary} imgClass="store-logo-img" wrapClass="flex items-center gap-2 shrink-0" nameClass="font-display font-extrabold text-xl tracking-tight text-stone-900" />
            <nav className="flex items-center gap-4 lg:gap-5 text-sm font-medium text-stone-700 shrink-0">
              {menu.map((m, i) => (
                <NavItem key={i} base={base} item={m} className="hover:text-stone-950 whitespace-nowrap" testid={`nav-${i}`} desktop />
              ))}
            </nav>
            <div className="flex-1" />
            <div className="shrink-0">
              <HeaderIcons {...iconProps} />
            </div>
          </div>
          {catNav.length > 0 && (
            <div className="hidden md:block border-t border-stone-100" data-testid="store-cat-nav">
              <div className="max-w-7xl mx-auto px-4 lg:px-8 flex items-center justify-between gap-4 h-11 overflow-x-auto no-scrollbar">
                {catNav.map((c) => (
                  <Link
                    key={c.id}
                    to={`${base}/shop?category=${c.slug}`}
                    className="text-[11px] uppercase tracking-[0.16em] font-semibold text-stone-600 hover:text-stone-950 whitespace-nowrap"
                    data-testid={`store-cat-nav-${c.slug}`}
                  >
                    {c.name}
                  </Link>
                ))}
              </div>
            </div>
          )}
        </header>
      ) : (
        <header className={`${sticky ? "sticky top-0 z-[80]" : "relative z-[80]"} w-full max-w-full bg-white/90 backdrop-blur-md border-b border-stone-200/70`}>
          <style>{`
            .store-logo-img { height: ${logoHMobile}px; width: auto; max-width: min(72vw, 280px); object-fit: contain; }
            @media (min-width: 768px) {
              .store-logo-img { height: ${logoHDesktop}px; max-width: min(42vw, 360px); }
            }
          `}</style>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 min-h-16 py-2 flex items-center gap-3 sm:gap-4 min-w-0 w-full">
            <MobileMenuSheet {...menuSheetProps} />
            <BrandMark info={brandInfo} primary={primary} imgClass="store-logo-img" wrapClass="flex items-center gap-2 sm:gap-3" />
            {zeploOnClassic ? (
              <OndemandPinChip slug={slug} onChange={() => window.dispatchEvent(new Event("ondemand-pin-edit"))} />
            ) : null}
            <nav className="hidden md:flex items-center gap-6 ml-6 text-sm text-stone-700">
              {menu.map((m, i) => (
                <NavItem key={i} base={base} item={m} className="hover:text-stone-900 font-medium" testid={`nav-${i}`} desktop />
              ))}
            </nav>
            <div className="ml-auto">
              <HeaderIcons {...iconProps} />
            </div>
          </div>
        </header>
      )}

      <main className={`min-h-[60vh] ${(boxy || cosmos) ? "pb-16 md:pb-0" : ""}`}>
        {children}
        <StorefrontBlogSection config={config} />
        {cosmos ? <CosmosNewsletter config={config} /> : null}
      </main>

      {ondemand ? (
        <footer className="mt-0 w-full bg-white border-t border-stone-100 text-stone-600" data-testid="store-footer-ondemand">
          <div className="max-w-[90rem] mx-auto px-4 sm:px-6 lg:px-10 py-10 sm:py-14 grid grid-cols-1 md:grid-cols-[1.1fr_repeat(2,minmax(0,1fr))] gap-10">
            <div data-testid="ondemand-footer-brand">
              <FooterBrand info={brandInfo} primary={primary} />
              <p className="mt-3 text-sm leading-relaxed text-stone-500 max-w-xs">{footer.about || "Quick commerce, delivered in minutes."}</p>
              <div className="mt-4 flex items-center gap-2" data-testid="ondemand-footer-socials">
                {socials.instagram ? <a href={socials.instagram} target="_blank" rel="noopener noreferrer" className="w-8 h-8 rounded-full border inline-flex items-center justify-center" data-testid="footer-social-instagram" aria-label="Instagram"><Instagram className="w-4 h-4" /></a> : null}
                {socials.twitter ? <a href={socials.twitter} target="_blank" rel="noopener noreferrer" className="w-8 h-8 rounded-full border inline-flex items-center justify-center" data-testid="footer-social-twitter" aria-label="X"><Twitter className="w-4 h-4" /></a> : null}
                {socials.facebook ? <a href={socials.facebook} target="_blank" rel="noopener noreferrer" className="w-8 h-8 rounded-full border inline-flex items-center justify-center" data-testid="footer-social-facebook" aria-label="Facebook"><Facebook className="w-4 h-4" /></a> : null}
                {socials.youtube ? <a href={socials.youtube} target="_blank" rel="noopener noreferrer" className="w-8 h-8 rounded-full border inline-flex items-center justify-center" data-testid="footer-social-youtube" aria-label="YouTube"><Youtube className="w-4 h-4" /></a> : null}
              </div>
              <div className="mt-6 text-xs text-stone-400">{footer.copyright_text || `© ${new Date().getFullYear()} ${info.name}.`}</div>
            </div>
            {columns.slice(0, 2).map((col, ci) => (
              <div key={ci} data-testid={`footer-col-${ci}`}>
                <div className="font-bold mb-4 text-stone-900">{col.title}</div>
                <div className="space-y-2 text-sm">
                  {(col.links || []).map((lk, li) => (
                    <SmartLink key={li} base={base} item={lk} className="block text-stone-600 hover:text-stone-900" testid={`footer-col-${ci}-lk-${li}`} />
                  ))}
                </div>
              </div>
            ))}
          </div>
          <div className="border-t border-stone-100 py-4 px-4 text-center text-xs text-stone-400">
            <PoweredByCredit config={config} className="font-mono-nums" nameClass="font-semibold text-stone-500" />
          </div>
        </footer>
      ) : cosmos ? (
        <footer className="mt-0 w-full bg-white border-t border-stone-100 text-stone-600" data-testid="store-footer-cosmos">
          <div className="max-w-[90rem] mx-auto px-4 sm:px-6 lg:px-10 py-10 sm:py-14">
            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-8 lg:gap-16">
              <div className="shrink-0" data-testid="cosmos-footer-brand">
                <FooterBrand info={brandInfo} primary={primary} />
              </div>
              <p className="flex-1 text-sm leading-relaxed text-stone-500">{footer.about || "We are a performance driven brand. Tell customers about your products in Footer → About."}</p>
              <div className="flex items-center gap-2 shrink-0 lg:ml-auto" data-testid="cosmos-footer-socials">
                {socials.facebook ? <a href={socials.facebook} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full text-white inline-flex items-center justify-center" style={{ background: primary }} data-testid="footer-social-facebook" aria-label="Facebook"><Facebook className="w-4 h-4" /></a> : null}
                {socials.twitter ? <a href={socials.twitter} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full text-white inline-flex items-center justify-center" style={{ background: primary }} data-testid="footer-social-twitter" aria-label="X"><Twitter className="w-4 h-4" /></a> : null}
                {socials.instagram ? <a href={socials.instagram} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full text-white inline-flex items-center justify-center" style={{ background: primary }} data-testid="footer-social-instagram" aria-label="Instagram"><Instagram className="w-4 h-4" /></a> : null}
                {socials.youtube ? <a href={socials.youtube} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full text-white inline-flex items-center justify-center" style={{ background: primary }} data-testid="footer-social-youtube" aria-label="YouTube"><Youtube className="w-4 h-4" /></a> : null}
              </div>
            </div>
            <div className="mt-10 grid grid-cols-2 gap-8 text-sm md:grid-cols-[repeat(auto-fit,minmax(160px,1fr))]">
              {columns.slice(0, 4).map((col, ci) => (
                <div key={ci} data-testid={`footer-col-${ci}`}>
                  <div className="font-bold mb-4" style={{ color: primary }}>{col.title}</div>
                  <div className="space-y-2">
                    {(col.links || []).map((lk, li) => (
                      <SmartLink key={li} base={base} item={lk} className="block text-stone-600 hover:text-stone-900" testid={`footer-col-${ci}-lk-${li}`} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-10 flex flex-col md:flex-row md:items-center gap-4 md:gap-10 text-sm" data-testid="cosmos-footer-contact">
              {(contact.email || info.email) ? (
                <a href={`mailto:${contact.email || info.email}`} className="inline-flex items-center gap-2 hover:text-stone-900" data-testid="cosmos-footer-email">
                  <span className="w-8 h-8 rounded-full text-white inline-flex items-center justify-center" style={{ background: primary }}><Mail className="w-4 h-4" /></span>
                  {contact.email || info.email}
                </a>
              ) : null}
              {(contact.phone || info.phone) ? (
                <a href={`tel:${contact.phone || info.phone}`} className="inline-flex items-center gap-2 hover:text-stone-900" data-testid="cosmos-footer-phone">
                  <span className="w-8 h-8 rounded-full text-white inline-flex items-center justify-center" style={{ background: primary }}><Phone className="w-4 h-4" /></span>
                  {contact.phone || info.phone}
                </a>
              ) : null}
              {(contact.address || info.address) ? (
                <div className="inline-flex items-start gap-2" data-testid="cosmos-footer-address">
                  <span className="w-8 h-8 rounded-full text-white inline-flex items-center justify-center shrink-0" style={{ background: primary }}><MapPin className="w-4 h-4" /></span>
                  <span>{contact.address || info.address}</span>
                </div>
              ) : null}
            </div>
          </div>
          <div className="border-t border-stone-100 py-5 px-4 sm:px-6 lg:px-10">
            <div className="max-w-[90rem] mx-auto flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-stone-400">
              <div>{footer.copyright_text || `© ${new Date().getFullYear()} ${info.name}.`}</div>
              <div className="flex flex-wrap items-center justify-center gap-1.5 text-[10px] font-bold text-stone-500" data-testid="cosmos-footer-pay">
                {["UPI", "RuPay", "VISA", "Mastercard"].map((p) => (
                  <span key={p} className="px-2 py-1 rounded border border-stone-200 bg-white">{p}</span>
                ))}
              </div>
            </div>
          </div>
        </footer>
      ) : carparts ? (
        <footer className="mt-0 text-white bg-black border-t-4" style={{ borderTopColor: primary }} data-testid="store-footer-carparts">
          <div className="max-w-[90rem] mx-auto px-4 sm:px-6 lg:px-10 py-12 sm:py-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-[1.15fr_1fr_repeat(3,minmax(0,0.85fr))] gap-10 text-sm text-white/70">
            <div data-testid="carparts-footer-about">
              <FooterBrand info={brandInfo} primary={primary} dark />
              <p className="mt-4 leading-relaxed max-w-xs">{footer.about || "We stock a wide range of auto parts and accessories. Tell customers about your brand in Footer → About."}</p>
              <div className="mt-6 font-extrabold text-white text-xs tracking-wide">Connect with us:</div>
              <SocialRow socials={socials} dark />
            </div>
            <div data-testid="carparts-footer-contact">
              <div className="font-extrabold text-white mb-4 uppercase tracking-wide text-xs">Contact us</div>
              <div className="space-y-3">
                {(contact.phone || info.phone) ? (
                  <a href={`tel:${contact.phone || info.phone}`} className="flex items-center gap-2 hover:text-white" data-testid="carparts-footer-phone">
                    <Phone className="w-4 h-4" style={{ color: primary }} />
                    <span>Toll free: <span className="font-semibold" style={{ color: primary }}>{contact.phone || info.phone}</span></span>
                  </a>
                ) : null}
                {info.whatsapp ? (
                  <a href={`https://wa.me/${info.whatsapp.replace(/[^0-9]/g, "")}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:text-white" data-testid="carparts-footer-chat">
                    <MessageSquareText className="w-4 h-4" style={{ color: primary }} />
                    <span>Live chat now!</span>
                    <span className="ml-1 inline-flex items-center h-6 px-2 rounded-full bg-white text-stone-900 text-[10px] font-extrabold uppercase">Live Chat</span>
                  </a>
                ) : null}
                {(contact.email || info.email) ? (
                  <a href={`mailto:${contact.email || info.email}`} className="flex items-center gap-2 hover:text-white" data-testid="carparts-footer-email">
                    <Mail className="w-4 h-4" style={{ color: primary }} />
                    <span>Email: {contact.email || info.email}</span>
                  </a>
                ) : null}
                {contact.hours ? (
                  <div>
                    <div className="font-extrabold text-white mt-3 mb-1">Business Hours:</div>
                    <div className="whitespace-pre-line">{contact.hours}</div>
                  </div>
                ) : null}
              </div>
            </div>
            {columns.slice(0, 3).map((col, ci) => (
              <div key={ci} data-testid={`footer-col-${ci}`}>
                <div className="font-extrabold text-white mb-4 uppercase tracking-wide text-xs">{col.title}</div>
                <div className="space-y-2">
                  {(col.links || []).map((lk, li) => (
                    <SmartLink key={li} base={base} item={lk} className="block hover:text-white" testid={`footer-col-${ci}-lk-${li}`} />
                  ))}
                </div>
              </div>
            ))}
          </div>
          <div className="border-t border-white/10 py-5 px-4 sm:px-6 lg:px-10">
            <div className="max-w-[90rem] mx-auto flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-white/55">
              <div>{footer.copyright_text || `Copyright © ${new Date().getFullYear()} ${info.name || "store"}. All rights reserved.`}</div>
              <div className="flex flex-wrap items-center justify-center gap-1.5" data-testid="carparts-footer-pay">
                {["Amex", "PayPal", "Discover", "Visa", "Mastercard"].map((p) => (
                  <span key={p} className="px-1.5 py-0.5 rounded bg-white/10 text-[10px] font-bold tracking-wide">{p}</span>
                ))}
              </div>
              <PoweredByCredit config={config} className="text-white/50" nameClass="font-semibold text-white/70" />
            </div>
          </div>
        </footer>
      ) : furnis ? (
        <footer className="mt-0 text-stone-700" style={{ background: "#F4F4F2" }} data-testid="store-footer-furnis">
          <div className="max-w-[90rem] mx-auto px-4 sm:px-6 lg:px-10 py-12 sm:py-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-10">
            <div>
              <div className="font-display italic text-xl text-stone-800">About us</div>
              <p className="furnis-sans mt-4 text-sm leading-relaxed text-stone-500">{footer.about || "Use this text area to tell your customers about your brand and vision. You can change it in the theme editor."}</p>
            </div>
            {columns.slice(0, 3).map((col, ci) => (
              <div key={ci} data-testid={`footer-col-${ci}`}>
                <div className="font-display italic text-xl text-stone-800 mb-4">{col.title}</div>
                <div className="space-y-2 furnis-sans text-sm">
                  {(col.links || []).map((lk, li) => (
                    <SmartLink key={li} base={base} item={lk} className="block text-stone-500 hover:text-stone-800" testid={`footer-col-${ci}-lk-${li}`} />
                  ))}
                </div>
              </div>
            ))}
            <div>
              <div className="font-display italic text-xl text-stone-800">Sign up &amp; Get 20% Off</div>
              <p className="furnis-sans mt-3 text-sm text-stone-500">Join the {info.name || "store"} family and enjoy exclusive benefits like early access and special discounts.</p>
              <div className="mt-5">
                <FurnisNewsletter />
              </div>
              <label className="mt-4 flex items-start gap-2 furnis-sans text-[11px] text-stone-500 leading-relaxed">
                <input type="checkbox" className="mt-0.5" readOnly tabIndex={-1} />
                <span>By subscribing you agree to the Terms of Use &amp; Privacy Policy.</span>
              </label>
            </div>
          </div>
          <div className="border-t border-stone-200/80 py-5 px-4 sm:px-6 lg:px-10">
            <div className="max-w-[90rem] mx-auto flex flex-col sm:flex-row items-center justify-between gap-3 furnis-sans text-xs text-stone-500">
              <div>{footer.copyright_text || `© ${new Date().getFullYear()}, ${info.name || "store"}`}</div>
              <PoweredByCredit config={config} className="font-mono-nums" nameClass="font-semibold text-stone-700" />
            </div>
          </div>
        </footer>
      ) : bigshow ? (
        <footer className="mt-0 text-white" style={{ background: primary }} data-testid="store-footer-bigshow">
          <div className="max-w-[90rem] mx-auto px-4 sm:px-6 lg:px-10 py-10 sm:py-14 grid grid-cols-1 lg:grid-cols-[minmax(16rem,22rem)_1fr] gap-10 border-b border-white/10">
            <div>
              <h3 className="text-xl font-extrabold">Subscribe to our emails</h3>
              <div className="mt-4">
                <BigShowNewsletter />
              </div>
              <p className="mt-3 text-[11px] text-white/50 max-w-sm">By signing up you agree to the terms and conditions, privacy policy.</p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-sm">
              {columns.slice(0, 4).map((col, ci) => (
                <div key={ci} data-testid={`footer-col-${ci}`}>
                  <div className="font-bold mb-4">{col.title}</div>
                  <div className="space-y-2">
                    {(col.links || []).map((lk, li) => (
                      <SmartLink key={li} base={base} item={lk} className="block text-white/70 hover:text-white" testid={`footer-col-${ci}-lk-${li}`} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="max-w-[90rem] mx-auto px-4 sm:px-2 overflow-hidden" data-testid="bigshow-footer-wordmark">
            <div className="bigshow-wordmark text-white uppercase truncate">{info.name || "Store"}</div>
          </div>
          <div className="border-t border-white/10 py-5 px-4 sm:px-6 lg:px-10">
            <div className="max-w-[90rem] mx-auto flex flex-col lg:flex-row items-center justify-between gap-4 text-xs text-white/60">
              <div>{footer.copyright_text || `© ${new Date().getFullYear()} ${info.name || "store"}`}</div>
              <PoweredByCredit config={config} className="font-mono-nums" nameClass="font-semibold text-white/80" />
              <div className="flex flex-wrap items-center justify-center gap-1.5 text-[10px] font-bold" data-testid="bigshow-footer-pay">
                {["Visa", "Mastercard", "Amex", "PayPal", "Discover"].map((p) => (
                  <span key={p} className="px-2 py-1 rounded bg-white text-stone-700">{p}</span>
                ))}
              </div>
            </div>
          </div>
        </footer>
      ) : quicksto ? (
        <footer className="mt-0 text-white" style={{ background: primary }} data-testid="store-footer-quicksto">
          <div className="max-w-[90rem] mx-auto px-4 sm:px-6 lg:px-10 py-10 sm:py-12 flex flex-col lg:flex-row lg:items-center justify-between gap-6 border-b border-white/15">
            <div className="max-w-xl">
              <h3 className="text-xl sm:text-2xl font-extrabold">Join the {info.name || "QuickSto"} Club!</h3>
              <p className="mt-2 text-sm text-white/70">Whether you are welcoming new contacts or sharing the latest news, you can make your business look good in just a few clicks.</p>
            </div>
            <div className="w-full lg:w-auto">
              <QuickstoNewsletter primary={primary} secondary={secondary} />
              <p className="mt-2 text-[11px] text-white/55 max-w-md">By subscribing you agree to our Terms &amp; Conditions and Privacy &amp; Cookies Policy.</p>
            </div>
          </div>
          <div className="max-w-[90rem] mx-auto px-4 sm:px-6 lg:px-10 py-12 sm:py-14 grid grid-cols-2 md:grid-cols-4 gap-8 text-sm">
            <div className="col-span-2 md:col-span-1">
              <FooterBrand info={brandInfo} primary="#ffffff" dark />
              {(contact.address || info.address) ? <p className="mt-4 text-white/70 text-sm leading-relaxed">{contact.address || info.address}</p> : null}
              {(contact.email || info.email) ? (
                <a href={`mailto:${contact.email || info.email}`} className="mt-3 block text-white/80 hover:text-white break-all">{contact.email || info.email}</a>
              ) : null}
            </div>
            {columns.slice(0, 3).map((col, ci) => (
              <div key={ci} data-testid={`footer-col-${ci}`}>
                <div className="font-bold mb-4">{col.title}</div>
                <div className="space-y-2">
                  {(col.links || []).map((lk, li) => (
                    <SmartLink key={li} base={base} item={lk} className="block text-white/70 hover:text-white" testid={`footer-col-${ci}-lk-${li}`} />
                  ))}
                </div>
              </div>
            ))}
          </div>
          <div className="border-t border-white/15 py-5 px-4 sm:px-6 lg:px-10">
            <div className="max-w-[90rem] mx-auto flex flex-col lg:flex-row items-center justify-between gap-4 text-xs text-white/60">
              <div>{footer.copyright_text || `Copyright ${new Date().getFullYear()} © ${info.name || "store"}. All right reserved.`}</div>
              <PoweredByCredit config={config} className="font-mono-nums" nameClass="font-semibold text-white/80" />
              <div className="flex flex-wrap items-center justify-center gap-1.5 text-[10px] font-bold" data-testid="quicksto-footer-pay">
                {["Visa", "Mastercard", "PayPal", "Skrill"].map((p) => (
                  <span key={p} className="px-2 py-1 rounded bg-white text-stone-700">{p}</span>
                ))}
              </div>
            </div>
          </div>
        </footer>
      ) : locally ? (
        <footer className="mt-0 text-white" style={{ background: primary }} data-testid="store-footer-locally">
          <div className="max-w-[90rem] mx-auto px-4 sm:px-6 lg:px-10 py-10 sm:py-12 flex flex-col lg:flex-row lg:items-center justify-between gap-6 border-b border-white/15">
            <div className="max-w-xl">
              <h3 className="text-xl sm:text-2xl font-extrabold">Join the {info.name || "Locally"} Club!</h3>
              <p className="mt-2 text-sm text-white/70">Whether you are welcoming new contacts or sharing the latest news, you can make your business look good in just a few clicks.</p>
            </div>
            <div className="w-full lg:w-auto">
              <LocallyNewsletter primary={primary} secondary={secondary} />
              <p className="mt-2 text-[11px] text-white/55 max-w-md">By subscribing you agree to our Terms &amp; Conditions and Privacy &amp; Cookies Policy.</p>
            </div>
          </div>
          <div className="max-w-[90rem] mx-auto px-4 sm:px-6 lg:px-10 py-12 sm:py-14 grid grid-cols-2 md:grid-cols-4 gap-8 text-sm">
            <div className="col-span-2 md:col-span-1">
              <FooterBrand info={brandInfo} primary="#ffffff" dark />
              {(contact.address || info.address) ? <p className="mt-4 text-white/70 text-sm leading-relaxed">{contact.address || info.address}</p> : null}
              {(contact.email || info.email) ? (
                <a href={`mailto:${contact.email || info.email}`} className="mt-3 block text-white/80 hover:text-white break-all">{contact.email || info.email}</a>
              ) : null}
              {(socials.instagram || socials.facebook || socials.youtube || socials.twitter || socials.threads) ? (
                <div data-testid="locally-footer-socials"><SocialRow socials={socials} dark /></div>
              ) : null}
            </div>
            {columns.slice(0, 3).map((col, ci) => (
              <div key={ci} data-testid={`footer-col-${ci}`}>
                <div className="font-bold mb-4">{col.title}</div>
                <div className="space-y-2">
                  {(col.links || []).map((lk, li) => (
                    <SmartLink key={li} base={base} item={lk} className="block text-white/70 hover:text-white" testid={`footer-col-${ci}-lk-${li}`} />
                  ))}
                </div>
              </div>
            ))}
          </div>
          <div className="border-t border-white/15 py-5 px-4 sm:px-6 lg:px-10">
            <div className="max-w-[90rem] mx-auto flex flex-col lg:flex-row items-center justify-between gap-4 text-xs text-white/60">
              <div>{footer.copyright_text || `Copyright ${new Date().getFullYear()} © ${info.name || "store"}. All right reserved.`}</div>
              <PoweredByCredit config={config} className="font-mono-nums" nameClass="font-semibold text-white/80" />
              <div className="flex flex-wrap items-center justify-center gap-1.5 text-[10px] font-bold" data-testid="locally-footer-pay">
                {["Visa", "Mastercard", "PayPal", "Skrill"].map((p) => (
                  <span key={p} className="px-2 py-1 rounded bg-white text-stone-700">{p}</span>
                ))}
              </div>
            </div>
          </div>
        </footer>
      ) : beast ? (
        <footer className="mt-0 bg-black text-white" data-testid="store-footer-beast">
          <div className="max-w-[90rem] mx-auto px-4 sm:px-6 lg:px-10 py-12 sm:py-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 text-sm">
            <div>
              <FooterBrand info={brandInfo} primary="#ffffff" dark />
              <p className="mt-4 text-neutral-400 text-sm max-w-xs leading-relaxed">{footer.about || "We only carry designs we believe in ethically and aesthetically – original, authentic pieces that are made to last."}</p>
              <Link to={`${base}/`} className="mt-3 inline-block text-white underline underline-offset-4">Learn more</Link>
              <div className="mt-5 space-y-2 text-neutral-300">
                {(contact.address || info.address) ? (
                  <div className="flex items-start gap-2"><MapPin className="w-4 h-4 mt-0.5 shrink-0" />{contact.address || info.address}</div>
                ) : null}
                {(contact.phone || info.phone) ? (
                  <a href={`tel:${contact.phone || info.phone}`} className="flex items-center gap-2 hover:text-white"><Phone className="w-4 h-4 shrink-0" />{contact.phone || info.phone}</a>
                ) : null}
                {(contact.email || info.email) ? (
                  <a href={`mailto:${contact.email || info.email}`} className="flex items-center gap-2 hover:text-white break-all"><Mail className="w-4 h-4 shrink-0" />{contact.email || info.email}</a>
                ) : null}
              </div>
            </div>
            {columns.slice(0, 2).map((col, ci) => (
              <div key={ci} data-testid={`footer-col-${ci}`}>
                <div className="font-bold text-white mb-4">{col.title}</div>
                <div className="space-y-2">
                  {(col.links || []).map((lk, li) => (
                    <SmartLink key={li} base={base} item={lk} className="block text-neutral-400 hover:text-white" testid={`footer-col-${ci}-lk-${li}`} />
                  ))}
                </div>
              </div>
            ))}
            <div>
              <div className="font-bold text-white mb-2">Sign Up to Newsletter</div>
              <p className="text-neutral-400 text-sm mb-4">Subscribe for store updates and discounts.</p>
              <BeastNewsletter />
              <p className="mt-3 text-[11px] text-neutral-500 leading-relaxed">
                ***By entering the e-mail you accept the <span className="text-white font-semibold">terms and conditions</span> and the <span className="text-white font-semibold">privacy policy</span>.
              </p>
              {(socials.instagram || socials.facebook || socials.youtube || socials.twitter || socials.threads) ? (
                <SocialRow socials={socials} dark />
              ) : null}
            </div>
          </div>
          <div className="border-t border-white/10 py-5 px-4 sm:px-6 lg:px-10">
            <div className="max-w-[90rem] mx-auto flex flex-col lg:flex-row items-center justify-between gap-4 text-xs text-neutral-400">
              <div>{info.name ? `${info.name}` : "United States"}</div>
              <div>{footer.copyright_text || `© ${new Date().getFullYear()} ${info.name || "store"}. All rights reserved.`}</div>
              <div className="flex flex-wrap items-center justify-center gap-1.5 text-[10px] font-bold" data-testid="beast-footer-pay">
                {["Visa", "Mastercard", "Amex", "PayPal", "UPI"].map((p) => (
                  <span key={p} className="px-2 py-1 rounded border border-white/20 text-white/80">{p}</span>
                ))}
              </div>
            </div>
            <PoweredByCredit config={config} className="mt-3 text-center text-xs text-neutral-500" nameClass="font-semibold text-neutral-300" />
          </div>
        </footer>
      ) : various ? (
        <footer className="mt-8" style={{ background: "#F1F2E8" }} data-testid="store-footer-various">
          <div className="max-w-[90rem] mx-auto px-4 sm:px-6 lg:px-10 py-10 sm:py-12 flex flex-col lg:flex-row lg:items-center justify-between gap-6 border-b border-[color:color-mix(in_srgb,var(--store-primary,#1B4332)_10%,transparent)]">
            <div>
              <div className="text-sm" style={{ color: "var(--store-primary, #1B4332)" }}>Join our newsletter</div>
              <div className="mt-1 text-lg sm:text-xl font-extrabold" style={{ color: "var(--store-primary, #1B4332)" }}>Get all latest information on events, sales and offers.</div>
            </div>
            <VariousNewsletter />
          </div>
          <div className="max-w-[90rem] mx-auto px-4 sm:px-6 lg:px-10 py-12 sm:py-16 grid grid-cols-2 md:grid-cols-4 gap-10 text-sm" style={{ color: "var(--store-primary, #1B4332)" }}>
            <div className="col-span-2 md:col-span-1">
              <FooterBrand info={brandInfo} primary={primary} />
              {(contact.phone || info.phone) ? (
                <a href={`tel:${contact.phone || info.phone}`} className="mt-4 block text-xl font-extrabold" data-testid="various-footer-phone">{contact.phone || info.phone}</a>
              ) : null}
              <div className="mt-3 space-y-1 text-sm opacity-80">
                {(contact.address || info.address) ? <div>Address {contact.address || info.address}</div> : null}
                {(contact.email || info.email) ? <div>Email {contact.email || info.email}</div> : null}
                {contact.hours ? <div data-testid="various-footer-hours">{contact.hours}</div> : null}
              </div>
            </div>
            {columns.slice(0, 3).map((col, ci) => (
              <div key={ci} data-testid={`footer-col-${ci}`}>
                <div className="font-bold mb-4">{col.title}</div>
                <div className="space-y-2">
                  {(col.links || []).map((lk, li) => (
                    <SmartLink key={li} base={base} item={lk} className="block opacity-80 hover:opacity-100" testid={`footer-col-${ci}-lk-${li}`} />
                  ))}
                </div>
              </div>
            ))}
          </div>
          <p className="text-center italic font-serif text-2xl sm:text-3xl py-6 px-4" style={{ color: "var(--store-primary, #1B4332)" }} data-testid="various-footer-tagline">
            {footer.about || "This is everything you need in your pantry!"}
          </p>
          <div className="border-t border-[color:color-mix(in_srgb,var(--store-primary,#1B4332)_10%,transparent)] py-5 px-4 sm:px-6 lg:px-10">
            <div className="max-w-[90rem] mx-auto flex flex-col lg:flex-row items-center justify-between gap-4 text-xs" style={{ color: "var(--store-primary, #1B4332)" }}>
              <div>{footer.copyright_text || `© ${new Date().getFullYear()} ${info.name}.`}</div>
              <PoweredByCredit config={config} className="font-mono-nums" nameClass="font-semibold" />
              <div className="flex flex-wrap items-center justify-center gap-1.5 text-[10px] font-bold" data-testid="various-footer-pay">
                {["Visa", "Mastercard", "Amex", "PayPal", "UPI"].map((p) => (
                  <span key={p} className="px-2 py-1 rounded border bg-white" style={{ borderColor: "color-mix(in srgb, var(--store-primary, #1B4332) 20%, transparent)" }}>{p}</span>
                ))}
              </div>
            </div>
          </div>
        </footer>
      ) : mixy ? (
        <footer className="mt-8 bg-white border-t border-stone-100" data-testid="store-footer-mixy">
          <div className="max-w-[90rem] mx-auto px-4 sm:px-6 lg:px-10 py-12 sm:py-16 grid grid-cols-2 md:grid-cols-4 gap-10 text-sm">
            <div className="col-span-2 md:col-span-1">
              <FooterBrand info={brandInfo} primary={primary} />
              <p className="mt-4 text-stone-500 text-sm max-w-xs leading-relaxed">{footer.about || "Shop everything you need — from electronics to everyday essentials — all in one place."}</p>
              {(socials.instagram || socials.facebook || socials.youtube || socials.twitter || socials.threads) ? (
                <SocialRow socials={socials} />
              ) : null}
            </div>
            {columns.slice(0, 3).map((col, ci) => (
              <div key={ci} data-testid={`footer-col-${ci}`}>
                <div className="font-bold text-stone-900 mb-4">{col.title}</div>
                <div className="space-y-2">
                  {(col.links || []).map((lk, li) => (
                    <SmartLink key={li} base={base} item={lk} className="block text-stone-500 hover:text-stone-900" testid={`footer-col-${ci}-lk-${li}`} />
                  ))}
                </div>
              </div>
            ))}
          </div>
          <div className="border-t border-stone-100 py-5 px-4 sm:px-6 lg:px-10">
            <div className="max-w-[90rem] mx-auto flex flex-col lg:flex-row items-center justify-between gap-4 text-xs text-stone-400">
              <div>{footer.copyright_text || `© ${new Date().getFullYear()} ${info.name}.`}</div>
              <PoweredByCredit config={config} className="font-mono-nums" nameClass="font-semibold text-stone-500" />
              <div className="flex flex-wrap items-center justify-center gap-1.5 text-[10px] font-bold text-stone-500" data-testid="mixy-footer-pay">
                {["Visa", "Mastercard", "Amex", "PayPal", "UPI"].map((p) => (
                  <span key={p} className="px-2 py-1 rounded border border-stone-200 bg-white">{p}</span>
                ))}
              </div>
            </div>
          </div>
        </footer>
      ) : perfecto ? (
        <footer className="mt-0 text-white relative" data-testid="store-footer-perfecto" style={{ background: primary && primary !== "#15803D" ? primary : "#4a302c" }}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8 text-sm">
            <div className="col-span-2 md:col-span-1">
              <FooterBrand info={brandInfo} primary="#ffffff" dark />
              <p className="mt-4 text-white/70 text-sm max-w-[14rem] leading-relaxed">{footer.about || "Love, Joy & Comfort."}</p>
            </div>
            {columns.slice(0, 4).map((col, ci) => (
              <div key={ci} data-testid={`footer-col-${ci}`}>
                <div className="font-display text-base font-semibold text-white mb-4">{col.title}</div>
                <div className="space-y-2">
                  {(col.links || []).map((lk, li) => (
                    <SmartLink key={li} base={base} item={lk} className="block text-white/70 hover:text-white" testid={`footer-col-${ci}-lk-${li}`} />
                  ))}
                </div>
              </div>
            ))}
            <div>
              <div className="font-display text-base font-semibold text-white mb-4">{t("store.getInTouch")}</div>
              <div className="space-y-2 text-white/75">
                {(contact.email || info.email) && (
                  <a href={`mailto:${contact.email || info.email}`} className="flex items-start gap-2 hover:text-white break-all"><Mail className="w-4 h-4 mt-0.5 shrink-0" />{contact.email || info.email}</a>
                )}
                {(contact.phone || info.phone) && (
                  <a href={`tel:${contact.phone || info.phone}`} className="flex items-center gap-2 hover:text-white"><Phone className="w-4 h-4 shrink-0" />{contact.phone || info.phone}</a>
                )}
              </div>
            </div>
          </div>
          {(socials.instagram || socials.facebook || socials.youtube || socials.twitter || socials.threads) && (
            <div className="border-t border-white/10">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex flex-wrap items-center gap-4 text-sm">
                <span className="font-medium">Follow Us</span>
                <SocialRow socials={socials} dark />
              </div>
            </div>
          )}
          <div className="border-t border-white/10 py-4 px-4 sm:px-6 lg:px-8">
            <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-white/55">
              <div>{footer.copyright_text || `Copyright © ${new Date().getFullYear()} ${info.name}. All Rights Reserved.`}</div>
              <PoweredByCredit config={config} className="font-mono-nums" nameClass="font-semibold text-white/75" />
            </div>
          </div>
          <button
            type="button"
            aria-label="Back to top"
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            className="absolute right-4 bottom-24 sm:bottom-8 w-10 h-10 rounded-full bg-black text-white flex items-center justify-center"
            data-testid="perfecto-back-to-top"
          >
            <ChevronUp className="w-5 h-5" />
          </button>
        </footer>
      ) : boxy ? (
        <footer className="mt-0 text-white" data-testid="store-footer-boxy" style={{ background: primary }}>
          <div className="max-w-6xl mx-auto px-6 lg:px-10 py-12 sm:py-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 text-sm">
            <div>
              <Link to={`${brandInfo.base}/`} className="inline-flex" data-testid="footer-brand">
                {brandInfo.footer_logo_url || brandInfo.logo_url ? (
                  <span className="w-16 h-16 border-2 border-white p-1.5 flex items-center justify-center">
                    <img src={mediaUrl(brandInfo.footer_logo_url || brandInfo.logo_url)} alt={brandInfo.name} className="max-h-full max-w-full object-contain" data-testid="store-footer-logo-img" />
                  </span>
                ) : (
                  <span className="w-16 h-16 border-2 border-white flex items-center justify-center font-display text-xl tracking-widest">{(info.name || "S").slice(0, 2).toUpperCase()}</span>
                )}
              </Link>
            </div>
            <div data-testid="boxy-footer-address">
              <div className="font-display text-[11px] uppercase tracking-[0.18em] text-white/80 mb-4">Address</div>
              <div className="text-white text-[13px] leading-relaxed space-y-1">
                <div className="font-semibold uppercase">{info.name}</div>
                {(contact.address || info.address) ? <div>{contact.address || info.address}</div> : null}
                {footer.about && !(contact.address || info.address) ? <div>{footer.about}</div> : null}
                {(contact.email || info.email) ? <a href={`mailto:${contact.email || info.email}`} className="block hover:underline break-all">{contact.email || info.email}</a> : null}
                {(contact.phone || info.phone) ? <a href={`tel:${contact.phone || info.phone}`} className="block hover:underline">{contact.phone || info.phone}</a> : null}
              </div>
            </div>
            <div data-testid="boxy-footer-info">
              <div className="font-display text-[11px] uppercase tracking-[0.18em] text-white/80 mb-4">{columns[0]?.title || "Information"}</div>
              <div className="space-y-2">
                {columns.flatMap((col, ci) => (col.links || []).map((lk, li) => (
                  <SmartLink key={`${ci}-${li}`} base={base} item={lk} className="block text-white/90 hover:text-white" testid={`footer-col-${ci}-lk-${li}`} />
                )))}
              </div>
            </div>
            <div data-testid="boxy-footer-benefits">
              <div className="font-display text-[11px] uppercase tracking-[0.18em] text-white/80 mb-4">Exclusive benefits</div>
              <BoxyNewsletter />
              <p className="mt-4 text-[12px] text-white/75 leading-relaxed">Subscribe to our newsletter to know our inside updates on new collections, offers, and many more.</p>
              {(socials.instagram || socials.facebook || socials.youtube || socials.twitter || socials.threads) ? (
                <SocialRow socials={socials} dark />
              ) : null}
            </div>
          </div>
          <div className="px-6 lg:px-10 pb-20 md:pb-8 max-w-6xl mx-auto text-[12px] text-white/70 space-y-1">
            <div>{footer.copyright_text || `© ${new Date().getFullYear()}, ${info.name}. All rights reserved.`}</div>
            <PoweredByCredit config={config} className="" nameClass="font-semibold text-white/85" />
          </div>
        </footer>
      ) : medico ? (
        <footer className="mt-0 text-[#1f2933]" data-testid="store-footer-medico" style={{ background: "#F5FBF7" }}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 grid grid-cols-2 md:grid-cols-4 gap-8 sm:gap-10 text-sm">
            <div data-testid="medico-footer-contact">
              <div className="font-bold text-stone-900 mb-3">Contact Us</div>
              {(contact.email || info.email) && <a href={`mailto:${contact.email || info.email}`} className="block text-stone-600 hover:text-stone-900 break-all mb-1">{contact.email || info.email}</a>}
              {(contact.phone || info.phone) && <a href={`tel:${contact.phone || info.phone}`} className="block text-stone-600 hover:text-stone-900 mb-3">{contact.phone || info.phone}</a>}
              {columns[0] ? (
                <>
                  <div className="font-bold text-stone-900 mb-3 mt-6">{columns[0].title}</div>
                  <div className="space-y-2">
                    {(columns[0].links || []).map((lk, li) => (
                      <SmartLink key={li} base={base} item={lk} className="block text-stone-600 hover:text-stone-900" testid={`footer-col-0-lk-${li}`} />
                    ))}
                  </div>
                </>
              ) : null}
            </div>
            {columns.slice(1, 3).map((col, ci) => (
              <div key={ci} data-testid={`footer-col-${ci + 1}`}>
                <div className="font-bold text-stone-900 mb-3">{col.title}</div>
                <div className="space-y-2">
                  {(col.links || []).map((lk, li) => (
                    <SmartLink key={li} base={base} item={lk} className="block text-stone-600 hover:text-stone-900" testid={`footer-col-${ci + 1}-lk-${li}`} />
                  ))}
                </div>
              </div>
            ))}
            <div data-testid="medico-footer-pay">
              <div className="font-bold text-stone-900 mb-3">Pay securely using</div>
              <div className="flex flex-wrap gap-1.5 text-[10px] font-bold text-stone-500">
                {["Visa", "Mastercard", "RuPay", "UPI"].map((p) => (
                  <span key={p} className="px-2 py-1 rounded border border-stone-200 bg-white">{p}</span>
                ))}
              </div>
              <div className="font-bold text-stone-900 mb-2 mt-6">Show us some love on</div>
              {(socials.instagram || socials.facebook || socials.youtube || socials.twitter || socials.threads) ? (
                <SocialRow socials={socials} mint />
              ) : null}
            </div>
          </div>
          <div className="flex justify-center pb-8">
            <FooterBrand info={brandInfo} primary={primary} />
          </div>
          <div className="bg-[#2D2D2D] text-white text-center text-xs py-3 px-4 space-y-1">
            <div>{footer.copyright_text || `Copyright © ${new Date().getFullYear()}, ${info.name}. All rights reserved.`}</div>
            <PoweredByCredit config={config} className="font-mono-nums text-white/75" nameClass="font-semibold text-white" />
          </div>
        </footer>
      ) : modern ? (
        <footer className={`${config.single_product_active ? "mt-0" : "mt-0"} bg-neutral-950 text-white/80`} data-testid="store-footer-modern">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 grid grid-cols-2 md:grid-cols-4 gap-10 text-sm">
            <div className="col-span-2 md:col-span-1">
              <FooterBrand info={brandInfo} primary={primary} dark />
              <p className="mt-4 text-white/55 text-sm max-w-xs leading-relaxed">{footer.about || "Thoughtfully curated products, delivered fresh to your doorstep."}</p>
              {(socials.instagram || socials.facebook || socials.youtube || socials.twitter || socials.threads) && (
                <SocialRow socials={socials} dark />
              )}
            </div>
            {columns.map((col, ci) => (
              <div key={ci} data-testid={`footer-col-${ci}`}>
                <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-white mb-4">{col.title}</div>
                <div className="space-y-2.5">
                  {(col.links || []).map((lk, li) => (
                    <SmartLink key={li} base={base} item={lk} className="block text-white/60 hover:text-white" testid={`footer-col-${ci}-lk-${li}`} />
                  ))}
                </div>
              </div>
            ))}
            <div>
              <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-white mb-4">{t("store.getInTouch")}</div>
              <div className="space-y-2 text-white/70">
                {(contact.email || info.email) && <a href={`mailto:${contact.email || info.email}`} className="block hover:text-white break-all">{contact.email || info.email}</a>}
                {(contact.phone || info.phone) && <a href={`tel:${contact.phone || info.phone}`} className="block hover:text-white">{contact.phone || info.phone}</a>}
                {(contact.whatsapp || info.whatsapp) && (
                  <a href={`https://wa.me/${(contact.whatsapp || info.whatsapp).replace(/[^0-9]/g, "")}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 mt-1 px-3 py-1.5 rounded-full text-white text-xs font-semibold" style={{ background: primary }} data-testid="footer-whatsapp">
                    <MessageSquareText className="w-3.5 h-3.5" /> {t("store.whatsappUs")}
                  </a>
                )}
                {(contact.address || info.address) && <div className="mt-1 text-xs text-white/45 leading-relaxed">{contact.address || info.address}</div>}
              </div>
            </div>
          </div>
          <div className="border-t border-white/10 py-5 px-4 sm:px-6 lg:px-8">
            <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-white/40">
              <div>{footer.copyright_text || `© ${new Date().getFullYear()} ${info.name}. All rights reserved.`}</div>
              <PoweredByCredit config={config} className="font-mono-nums" nameClass="font-semibold text-white/60" />
            </div>
          </div>
        </footer>
      ) : trendy ? (
        <footer className="mt-8 text-white" data-testid="store-footer-trendy" style={{ background: `linear-gradient(165deg, #1c1917 0%, ${primary} 140%)` }}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
            <div className="rounded-[2rem] bg-white/5 border border-white/10 p-8 sm:p-10 grid grid-cols-2 md:grid-cols-4 gap-10 text-sm">
              <div className="col-span-2 md:col-span-1">
                <FooterBrand info={brandInfo} primary={primary} dark />
                <p className="mt-4 text-white/60 text-sm max-w-xs leading-relaxed">{footer.about || "Thoughtfully curated products, delivered fresh to your doorstep."}</p>
                {(socials.instagram || socials.facebook || socials.youtube || socials.twitter || socials.threads) && (
                  <SocialRow socials={socials} dark />
                )}
              </div>
              {columns.map((col, ci) => (
                <div key={ci} data-testid={`footer-col-${ci}`}>
                  <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-white mb-4">{col.title}</div>
                  <div className="space-y-2.5">
                    {(col.links || []).map((lk, li) => (
                      <SmartLink key={li} base={base} item={lk} className="block text-white/60 hover:text-white" testid={`footer-col-${ci}-lk-${li}`} />
                    ))}
                  </div>
                </div>
              ))}
              <div>
                <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-white mb-4">{t("store.getInTouch")}</div>
                <div className="space-y-2 text-white/70">
                  {(contact.email || info.email) && <a href={`mailto:${contact.email || info.email}`} className="block hover:text-white break-all">{contact.email || info.email}</a>}
                  {(contact.phone || info.phone) && <a href={`tel:${contact.phone || info.phone}`} className="block hover:text-white">{contact.phone || info.phone}</a>}
                  {(contact.whatsapp || info.whatsapp) && (
                    <a href={`https://wa.me/${(contact.whatsapp || info.whatsapp).replace(/[^0-9]/g, "")}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 mt-1 px-3 py-1.5 rounded-full text-white text-xs font-semibold" style={{ background: primary }} data-testid="footer-whatsapp">
                      <MessageSquareText className="w-3.5 h-3.5" /> {t("store.whatsappUs")}
                    </a>
                  )}
                  {(contact.address || info.address) && <div className="mt-1 text-xs text-white/45 leading-relaxed">{contact.address || info.address}</div>}
                </div>
              </div>
            </div>
          </div>
          <div className="border-t border-white/10 py-5 px-4 sm:px-6 lg:px-8">
            <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-white/40">
              <div>{footer.copyright_text || `© ${new Date().getFullYear()} ${info.name}. All rights reserved.`}</div>
              <PoweredByCredit config={config} className="font-mono-nums" nameClass="font-semibold text-white/60" />
            </div>
          </div>
        </footer>
      ) : (
        <footer className={`${config.single_product_active ? "mt-0 border-t-0" : "mt-20 border-t border-stone-200"} bg-white`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid grid-cols-2 md:grid-cols-4 gap-8 text-sm text-stone-600">
            <div className="col-span-2 md:col-span-1">
              <FooterBrand info={brandInfo} primary={primary} />
              <p className="mt-3 text-stone-500 text-sm max-w-xs">{footer.about || "Thoughtfully curated products, delivered fresh to your doorstep."}</p>
              {(socials.instagram || socials.facebook || socials.youtube || socials.twitter || socials.threads) && (
                <SocialRow socials={socials} />
              )}
            </div>
            {columns.map((col, ci) => (
              <div key={ci} data-testid={`footer-col-${ci}`}>
                <div className="text-xs uppercase tracking-[0.2em] font-bold text-stone-900 mb-3">{col.title}</div>
                <div className="space-y-2">
                  {(col.links || []).map((lk, li) => (
                    <SmartLink key={li} base={base} item={lk} className="block hover:text-stone-900" testid={`footer-col-${ci}-lk-${li}`} />
                  ))}
                </div>
              </div>
            ))}
            <div>
              <div className="text-xs uppercase tracking-[0.2em] font-bold text-stone-900 mb-3">{t("store.getInTouch")}</div>
              <div className="space-y-2">
                {(contact.email || info.email) && <a href={`mailto:${contact.email || info.email}`} className="block hover:text-stone-900 break-all">{contact.email || info.email}</a>}
                {(contact.phone || info.phone) && <a href={`tel:${contact.phone || info.phone}`} className="block hover:text-stone-900">{contact.phone || info.phone}</a>}
                {(contact.whatsapp || info.whatsapp) && (
                  <a href={`https://wa.me/${(contact.whatsapp || info.whatsapp).replace(/[^0-9]/g, "")}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 mt-1 px-3 py-1.5 rounded-full text-white text-xs font-semibold" style={{ background: primary }} data-testid="footer-whatsapp">
                    <MessageSquareText className="w-3.5 h-3.5" /> {t("store.whatsappUs")}
                  </a>
                )}
                {(contact.address || info.address) && <div className="mt-1 text-xs text-stone-500 leading-relaxed">{contact.address || info.address}</div>}
              </div>
            </div>
          </div>
          <div className="border-t border-stone-100 py-5 px-4 sm:px-6 lg:px-8">
            <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-stone-400">
              <div>{footer.copyright_text || `© ${new Date().getFullYear()} ${info.name}. All rights reserved.`}</div>
              <PoweredByCredit config={config} className="font-mono-nums" nameClass="font-semibold text-stone-500" />
            </div>
          </div>
        </footer>
      )}
      {boxy ? (
        <nav className="md:hidden fixed bottom-0 inset-x-0 z-50 bg-white border-t border-stone-200 grid grid-cols-5 h-[3.6rem] pb-[env(safe-area-inset-bottom)]" data-testid="boxy-bottom-nav">
          {[
            { key: "home", node: (
              <Link to={`${base}/`} className="flex flex-col items-center justify-center gap-0.5 text-[9px] font-semibold uppercase tracking-[0.12em] text-stone-800" data-testid="boxy-nav-home">
                <Home className="w-5 h-5" strokeWidth={1.5} /> Home
              </Link>
            ) },
            { key: "menu", node: (
              <button type="button" onClick={() => setMenuOpen(true)} className="flex flex-col items-center justify-center gap-0.5 text-[9px] font-semibold uppercase tracking-[0.12em] text-stone-800" data-testid="boxy-nav-menu">
                <Menu className="w-5 h-5" strokeWidth={1.5} /> Menu
              </button>
            ) },
            { key: "search", node: (
              <div className="flex flex-col items-center justify-center gap-0.5 text-[9px] font-semibold uppercase tracking-[0.12em] text-stone-800">
                {showSearch ? <SearchToggle shopHref={shopHref} navigate={navigate} look="boxy" testId="boxy-nav-search" /> : <Search className="w-5 h-5" strokeWidth={1.5} />}
                <span>Search</span>
              </div>
            ) },
            { key: "cart", node: (
              <button type="button" onClick={openCart} className="relative flex flex-col items-center justify-center gap-0.5 text-[9px] font-semibold uppercase tracking-[0.12em] text-stone-800" data-testid="boxy-nav-cart">
                <ShoppingCart className="w-5 h-5" strokeWidth={1.5} />
                {count > 0 && <span className="absolute top-1 right-[22%] min-w-[14px] h-3.5 px-0.5 rounded-full bg-[var(--store-primary)] text-white text-[9px] font-bold">{count}</span>}
                Cart
              </button>
            ) },
            { key: "account", node: (
              <Link to={user ? `${base}/account` : `${base}/account/login`} className="flex flex-col items-center justify-center gap-0.5 text-[9px] font-semibold uppercase tracking-[0.12em] text-stone-800" data-testid="boxy-nav-account">
                <User className="w-5 h-5" strokeWidth={1.5} /> Account
              </Link>
            ) },
          ].map((item) => <div key={item.key} className="flex items-center justify-center">{item.node}</div>)}
        </nav>
      ) : cosmos ? (
        <nav className="md:hidden fixed bottom-0 inset-x-0 z-50 text-white grid grid-cols-5 h-[3.7rem] pb-[env(safe-area-inset-bottom)] rounded-t-2xl overflow-hidden" style={{ background: primary }} data-testid="cosmos-bottom-nav">
          <Link to={`${base}/`} className="flex flex-col items-center justify-center gap-0.5 text-[11px] font-semibold" data-testid="cosmos-nav-home">
            <Home className="w-5 h-5" strokeWidth={1.75} /> Home
          </Link>
          <button type="button" onClick={() => setMenuOpen(true)} className="flex flex-col items-center justify-center gap-0.5 text-[11px] font-semibold" data-testid="cosmos-nav-menu">
            <Menu className="w-5 h-5" strokeWidth={1.75} /> Menu
          </button>
          <div className="flex flex-col items-center justify-center gap-0.5 text-[11px] font-semibold">
            {showSearch ? <SearchToggle shopHref={shopHref} navigate={navigate} look="cosmos" testId="cosmos-nav-search" className="w-8 h-8 text-white" /> : <Search className="w-5 h-5" strokeWidth={1.75} />}
            <span>Search</span>
          </div>
          <Link to={`${base}/shop`} className="flex flex-col items-center justify-center gap-0.5 text-[11px] font-semibold" data-testid="cosmos-nav-shop">
            <LayoutGrid className="w-5 h-5" strokeWidth={1.75} /> Shop
          </Link>
          <button type="button" onClick={openCart} className="relative flex flex-col items-center justify-center gap-0.5 text-[11px] font-semibold" data-testid="cosmos-nav-cart">
            <ShoppingBag className="w-5 h-5" strokeWidth={1.75} />
            {count > 0 && <span className="absolute top-1 right-[22%] min-w-[14px] h-3.5 px-0.5 rounded-full bg-white text-[9px] font-bold" style={{ color: primary }}>{count}</span>}
            Cart
          </button>
        </nav>
      ) : null}
      <StorefrontCartDrawer />
      <StoreWhatsAppButton config={config} />
    </div>
  );
}
